<template>
    <Toast />
    <form @submit.prevent="submit()">
        <div class="p-d-flex p-jc-between">
            <p class="title">Account Management</p>
            <div>
                <Button v-if="currentStep === 1" label="Bulk Upload" class="button btn mr-1rem"  @click="openBulkUploadDialog"  />
            </div>
        </div>

        <!-- Partner Flow Stepper -->
        <div class="stepper-container">
            <div class="stepper">
                <div 
                    v-for="(step, index) in currentStepperSteps" 
                    :key="index"
                    class="stepper-step"
                    :class="getStepClass(index + 1)"
                    @click="navigateToStep(index + 1)"
                >
                    <div class="step-indicator">
                        <i v-if="adjustedCurrentStep > index + 1" class="pi pi-check step-check"></i>
                        <span v-else class="step-number">{{ getDisplayStepNumber(index + 1) }}</span>
                    </div>
                    <div class="step-label">{{ step }}</div>
                </div>
            </div>
        </div>

        <div class="card">

            <h4>{{ selectedPartner ? 'Edit Vendor' : 'Add Vendor' }}</h4>

            <!-- Partner Search Step (Step 1) -->
            <div v-if="currentStep === 1" class="partner-search-step">
                <PartnerSearch 
                    @partner-selected="onPartnerSelected"
                    @proceed-to-create="onProceedToCreate"
                />
            </div>
            
            <!-- Back to Previous Step Button -->
            <div v-if="currentStep > 1" class="back-to-search">
                <Button 
                    :label="getBackButtonLabel()" 
                    class="p-button-text" 
                    @click="goToPreviousStep"
                    icon="pi pi-arrow-left"
                />
            </div>
            
            <!-- Step 2 Title for Create New Partner -->
            <div v-if="currentStep === 2" class="step-title">
                <h4>{{ getStepTitle(2) }}</h4>
            </div>
            
            <!-- Step 3 Title for Existing Partner -->
            <div v-if="currentStep === 3" class="step-title">
                <h4>{{ getStepTitle(3) }}</h4>
            </div>
            
            <!-- Step 4 Title for Vendor Sharing (Visibility Settings) -->
            <div v-if="currentStep === 4" class="step-title">
                <h4>{{ getStepTitle(4) }}</h4>
            </div>

            <!-- Step 5 — AI Pre-Population (NEW, feature-flagged). Runs last
                 so the vendor account already exists by the time it renders. -->
            <div v-if="aiPrePopEnabled && currentStep === 5" class="step-title">
                <h4>{{ getStepTitle(5) }}</h4>
            </div>
            
            <!-- Account Form -->
            <div v-if="currentStep > 1" class="p-grid p-fluid">
                <!-- Step 2: Create New Partner - Limited Fields -->
                <div v-if="currentStep === 2" class="p-col-12 p-md-6">
                    <p class="field-title">Company Name</p>
                    <InputText 
                        type="text" 
                        v-model="v$.Name.$model" 
                        placeholder="company name"
                        :disabled="isNameFieldDisabled"
                        :class="{ 'disabled-input': isNameFieldDisabled }"
                    ></InputText>
                    <p v-for="error of v$.Name.$errors" :key="error.$uid" class="error-msg">
                        {{ 'Please enter the company name' }}
                    </p>
                </div>
                <div v-if="currentStep === 2 && platformName === 'ArenaCX' && !isChannel " class="p-col-12 p-md-6">
                    <p class="field-title">ArenaCX Quickbooks ID</p>
                    <InputText type="number" min="0" v-model="v$.quickbooksID.$model" placeholder="ArenaCX Quickbooks ID" @keypress="validateNumberInput">
                    </InputText>
                    <p v-for="error of v$.quickbooksID.$errors" :key="error.$uid" class="error-msg">
                        {{ error.$validator === 'required' ? 'Please enter the ArenaCX Quickbooks ID' : 
                           error.$validator === 'numeric' ? 'Only numbers are allowed' : 
                           'Please enter the ArenaCX Quickbooks ID' }}
                    </p>
                </div>
                <div v-if="currentStep === 2" class="p-col-12 p-md-6">
                    <p class="field-title">Description</p>
                    <InputText type="text" v-model="v$.description.$model" placeholder="description"></InputText>
                    <p v-for="error of v$.description.$errors" :key="error.$uid" class="error-msg">
                        {{ 'Please enter the description' }}
                    </p>
                </div>

                <!-- Next Button for Step 2 -->
                <div v-if="currentStep === 2" class="step2-navigation">
                    <Button 
                        label="Next" 
                        class="next-button" 
                        @click="goToStep3FromStep2"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        :disabled="!isStep2Valid"
                    />
                </div>

                <!-- Step 3: Company Contacts Section -->
                <div v-if="currentStep === 3" class="p-col-12">
                    <!-- Commission Rate and Notes Section -->
                    <div class="p-grid p-fluid" style="margin-bottom: 2rem;">
                        <div class="p-col-12 p-md-6">
                            <p class="field-title">Commission Rate *</p>
                            <InputText 
                                id="commissionRate"
                                v-model="commissionRate" 
                                placeholder="Enter commission rate"
                                type="number"
                                min="0"
                                step="0.01"
                                max="100"
                                class="w-full"
                                required
                                 @keypress="validateNumberInput"

                            />
                            <p v-for="error of v$.commissionRate.$errors" :key="error.$uid" class="error-msg">
                        {{ error.$validator === 'required' ? 'Please enter the Commission Rate' : 
                           error.$validator === 'numeric' ? 'Only numbers are allowed' : 
                           'Please enter the Commission Rate' }}
                    </p>
                        </div>
                        
                        <div class="p-col-12 p-md-6">
                            <p class="field-title">Notes</p>
                            <InputText 
                                id="notes"
                                v-model="notes" 
                                placeholder="Enter notes"
                                class="w-full"
                            />
                        </div>
                    </div>
                    
                    <div class="company-contacts-header">
                        <div class="contacts-title">
                            <h4>Company Contacts</h4>
                        </div>
                        <Button 
                            label="Add Contact" 
                            class="add-contact-button" 
                            @click="openContactDialog"
                            icon="pi pi-plus"
                        />
                    </div>
                    
                    <!-- Contacts Table -->
                     
                    <div class="table-wrap">
                        <table class="contacts-table">
                            <thead>
                                <tr>
                                    <th>Actions</th>
                                    <th>Name</th>
                                    <th>Title</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Primary Contact</th>
                                    <th>RFP Contributor</th>
                                    <th>RFP Invitation Recipient</th>
                                    <th>Escalation Contact</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(contact, index) in contacts"
                                    :key="index"
                                    :class="{ 'highlight-contact': isHighlighted(contact) }"
                                    :ref="el => isHighlighted(contact) && (highlightRowEl = el)"
                                >


                                    <td class="actions-column">
                                        <div class="actions-column-inner">
                                                <Button icon="pi pi-pencil" class="p-button-rounded p-button-warning mr-2"
                                                @click="editContact(index)" />
                                                <Button icon="pi pi-trash" class="p-button-rounded p-button-warning"
                                                @click="deleteContact(index)" />
                                        </div>
                                    </td>
                                    <td>{{ contact.name }}</td>
                                    <td>{{ contact.title }}</td>
                                    <td>
                                        {{ contact.email }}
                                        <EmailDeliverabilityBadge
                                            :status="contact.deliverability_status"
                                            :last-bounced-at="contact.last_bounced_at"
                                            :last-diagnostic="contact.last_diagnostic"
                                        />
                                    </td>
                                    <td>{{ contact.phone }}</td>
                                    <td class="center-column">
                                        <i :class="['pi', contact.isPrimaryContact ? 'pi-check' : 'pi-times']"></i>
                                    </td>
                                    <td class="center-column">
                                        <i :class="['pi', contact.isRFPContributor ? 'pi-check' : 'pi-times']"></i>
                                    </td>
                                    <td class="center-column">
                                        <i :class="['pi', contact.isRFPInvitationRecipient ? 'pi-check' : 'pi-times']"></i>
                                    </td>
                                    <td class="center-column">
                                        <i :class="['pi', contact.isEscalationContact ? 'pi-check' : 'pi-times']"></i>
                                    </td>
                                </tr>
                                <tr v-if="contacts.length === 0">
                                    <td colspan="9" class="no-contacts">
                                        No contacts added yet. Click "Add Contact" to get started.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    
                    <!-- Navigation Buttons for Step 3 -->
                    <div class="step-navigation">
                        <!-- Next Button for New Vendor Flow (Step 3 of 4) -->
                        <Button 
                            label="Next" 
                            class="next-button" 
                            @click="goToStep4"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            :disabled="!isStep3Valid"
                        />
                    </div>
                </div>

                <!-- Step 4: Vendor Sharing Section (Visibility Settings) -->
                <div v-if="currentStep === 4" class="p-col-12">
                    <div class="vendor-sharing-container">
                        <div class="sharing-header">
                            <p class="sharing-description">
                                Configure how vendor information is shared across the platform and with other partners.
                            </p>
                        </div>
                        
                        <!-- Channel Visibility Section -->
                        <div class="channel-visibility-section">
                            <h5>Channel Visibility</h5>
                            <p class="section-description">
                                Choose how your vendor is visible to other partners.
                            </p>
                            
                            <div class="visibility-options">
                                <div class="visibility-option">
                                    <RadioButton 
                                        id="public" 
                                        name="channelVisibility" 
                                        value="public" 
                                        v-model="vendorSharing.channelVisibility" 
                                    />
                                    <label for="public" class="visibility-label">Public</label>
                                    <p class="visibility-description">
                                        All channels are automatically visible in a read-only state. User cannot deselect channels.
                                    </p>
                                </div>
                                
                                <div class="visibility-option">
                                    <RadioButton 
                                        id="private" 
                                        name="channelVisibility" 
                                        value="private" 
                                        v-model="vendorSharing.channelVisibility" 
                                    />
                                    <label for="private" class="visibility-label">Private</label>
                                    <p class="visibility-description">
                                        No channels are shown because the account is private.
                                    </p>
                                </div>
                                
                                <div class="visibility-option">
                                    <RadioButton 
                                        id="select" 
                                        name="channelVisibility" 
                                        value="select" 
                                        v-model="vendorSharing.channelVisibility" 
                                    />
                                    <label for="select" class="visibility-label">Select</label>
                                    <p class="visibility-description">
                                        No channels are pre-selected. Show a list of all available channels with checkboxes.
                                    </p>
                                </div>
                            </div>
                            
                            <!-- Dynamic Channel Display Based on Selection -->
                            <div class="channel-display-section">
                                <!-- Public Mode -->
                                <div v-if="vendorSharing.channelVisibility === 'public'" class="public-channels">
                                    <h6>Public Channels (Read-Only)</h6>
                                    <div class="channel-list">
                                        <div v-for="channel in channels" :key="channel.subnetwork_id" class="channel-item public-channel">
                                            <i class="pi pi-eye" style="color: #00796b; margin-right: 0.5rem;"></i>
                                            <span>{{ channel.name }}</span>
                                            <span class="status-badge">Public</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Private Mode -->
                                <div v-if="vendorSharing.channelVisibility === 'private'" class="private-channels">
                                    <div class="private-message">
                                        <i class="pi pi-lock" style="color: #ef4444; font-size: 1.5rem; margin-bottom: 1rem;"></i>
                                        <h6>Private Account</h6>
                                        <p>Your vendor agreement won't be visible to other channels.</p>
                                    </div>
                                </div>
                                
                                <!-- Select Mode -->
                                <div v-if="vendorSharing.channelVisibility === 'select'" class="select-channels">
                                    <h6>Select Channels to Enable</h6>
                                    <div class="channel-selection-list">
                                        <div v-for="channel in channels" :key="channel.subnetwork_id" class="channel-selection-item">
                                            <Checkbox 
                                                v-model="channel.selectedForSharing" 
                                                :binary="true" 
                                                :inputId="'channel-' + channel.subnetwork_id"
                                                :disabled="vendorSharing.channelVisibility === 'select' && 
                                                    (channel.subnetwork_id === (isChannel ? subnetworkIdOfOtherChannels : defaultSubnetworkId))"
                                            />
                                            <label :for="'channel-' + channel.subnetwork_id" class="channel-selection-label">
                                                {{ channel.name }}
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        

                        
                        <!-- Navigation Buttons for Step 4 (Visibility) -->
                        <div class="step-navigation">
                            <Button
                                label="Previous"
                                class="previous-button"
                                @click="goToPreviousStep"
                                icon="pi pi-arrow-left"
                            />
                            <!-- Save Button for New Vendor Flow -->
                            <Button 
                                v-if="isNewVendor"
                                label="Save" 
                                class="complete-button" 
                                @click="saveNewVendor"
                                icon="pi pi-check"
                            />
                            <!-- Complete Button for Existing Vendor Flow -->
                            <Button 
                                v-if="!isNewVendor"
                                label="Complete" 
                                class="complete-button" 
                                @click="saveVendorRelationship"
                                icon="pi pi-check"
                            />
                        </div>
                    </div>
                </div>

                <!-- Step 5: AI Pre-Population (NEW, Optional, feature-flagged,
                     last step). By this point Visibility Settings has already
                     saved/created the vendor, so account_id is always
                     available -- both flows render AiPrePopulationStep
                     directly, no deferred placeholder needed. -->
                <div v-if="aiPrePopEnabled && currentStep === 5" class="p-col-12">
                    <AiPrePopulationStep
                        v-if="selectedPartner && selectedPartner.account_id"
                        ref="aiPrePopStep"
                        :partner-id="selectedPartner.account_id"
                        :qsid="seQuestionSetId"
                        :vendor-name="Name"
                        :is-new-vendor="isNewVendor"
                        @skip="finishVendorWizard"
                        @sources-changed="onSourcesChanged"
                        @run-state-changed="onAiRunStateChanged"
                    />
                    <!-- Navigation footer — Skip and Finish sit on the same
                         row. Skip lives here (not inside AiPrePopulationStep)
                         so it aligns with Finish.

                         Skip only shows before any run has started -- once a
                         run exists (completed, failed, or in progress) its
                         result is what determines the welcome email, so a
                         "Skip" button next to it would misleadingly imply
                         you can still discard those results. Finish is the
                         only option at that point, and stays disabled while
                         a run is in progress since the welcome email is
                         one-shot (see finishVendorWizard) -- finishing
                         mid-run would lock the vendor into the no-AI
                         template even though the run finishes moments
                         later, with no automatic follow-up email. -->
                    <div class="ai-prepop-nav-footer">
                        <small v-if="isAiRunInProgress" class="ai-prepop-run-wait-hint">
                            Waiting for AI Pre-Population to finish before you can continue…
                        </small>
                        <Button
                            v-if="!hasAiRunStarted"
                            label="Skip this step"
                            class="p-button-outlined ai-prepop-skip-btn"
                            :disabled="isAiRunInProgress"
                            @click="finishVendorWizard"
                        />
                        <Button
                            label="Finish"
                            class="p-button-primary ai-prepop-continue-btn"
                            :disabled="isAiRunInProgress"
                            @click="finishVendorWizard"
                        />
                    </div>
                </div>

            </div>
        </div>
    </form>

    <!-- Contact Dialog -->
    <Dialog :draggable="false"
        v-model:visible="showContactDialog" 
        modal 
        header="Contact Details" 
        :style="{ width: '40rem' }"
        :closable="true"
    >
        <div class="contact-form">
            <div class="form-field">
                <label for="contactName">Name *</label>
                <InputText 
                    id="contactName"
                    v-model="contactForm.name" 
                    placeholder="Enter name"
                    class="w-full"
                    required
                />
            </div>
            
            <div class="form-field">
                <label for="contactTitle">Title *</label>
                <InputText 
                    id="contactTitle"
                    v-model="contactForm.title" 
                    placeholder="Enter title"
                    class="w-full"
                    required
                />
            </div>
            
            <div class="form-field">
                <label for="contactEmail">Email *</label>
                <InputText 
                    id="contactEmail"
                    v-model="contactForm.email" 
                    placeholder="Enter email"
                    type="email"
                    class="w-full"
                    required
                />
            </div>
            
            <div class="form-field">
                <label for="contactPhone">Phone *</label>
                <InputText 
                    id="contactPhone"
                    v-model="contactForm.phone" 
                    placeholder="Enter phone number"
                    class="w-full"
                    required
                />
            </div>
            
            
            <div class="checkbox-group">
                <div class="checkbox-item">
                    <Checkbox 
                        v-model="contactForm.isPrimaryContact" 
                        :binary="true" 
                        inputId="primaryContact"
                        @change="handlePrimaryContactChange"
                    />
                    <label for="primaryContact">
                        Set as Primary Contact
                    </label>
                </div>
                
                <div class="checkbox-item">
                    <Checkbox 
                        v-model="contactForm.isRFPContributor" 
                        :binary="true" 
                        inputId="rfpContributor"
                        @change="handleRFPContributorChange"
                    />
                    <label for="rfpContributor">Set as RFP Contributor</label>
                </div>
                <div class="checkbox-item">
                    <Checkbox 
                        v-model="contactForm.isRFPInvitationRecipient" 
                        :binary="true" 
                        inputId="rfpInvitationRecipient"
                        @change="handleRFPInvitationRecipientChange"
                    />
                    <label for="rfpInvitationRecipient">Set as RFP Invitation Recipient</label>
                </div>
                <div class="checkbox-item">
                    <Checkbox 
                        v-model="contactForm.isEscalationContact" 
                        :binary="true" 
                        inputId="escalationContact"
                    />
                    <label for="escalationContact">Set as Escalation Contact</label>
                </div>
            </div>
        </div>
        
        <template #footer>
            <Button 
                label="Cancel" 
                class="dialog-cancel-button" 
                @click="closeContactDialog"
            />
            <Button 
                :label="editingContactIndex >= 0 ? 'Update' : 'Add'" 
                class="dialog-action-button" 
                @click="saveContact"
            />
        </template>
    </Dialog>

    <!-- Primary Contact Replacement Confirmation Dialog -->
    <Dialog :draggable="false"
        v-model:visible="showPrimaryContactReplaceDialog" 
        modal 
        header="A Primary Contact Already Exists." 
        :style="{ width: '500px' }"
        @hide="cancelPrimaryContactReplace"
    >
        <div class="confirmation-content">
            <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
            <span>
                You can either delete the existing contact or replace it. If you replace it, the current Primary Contact will be downgraded to an RFP Contributor.
            </span>
        </div>
        <template #footer>
            <Button 
                label="Cancel" 
                icon="pi pi-times" 
                @click="cancelPrimaryContactReplace" 
                class="p-button p-component p-button-outlined button" 
            />
            <Button 
                label="Delete Existing" 
                icon="pi pi-trash" 
                @click="deleteExistingPrimaryContact" 
                class="p-button p-component p-button-danger button" 
                style="width: 10rem !important;"
            />
            <Button 
                label="Replace" 
                icon="pi pi-check" 
                @click="confirmPrimaryContactReplace" 
                class="p-button-warn" 
            />
        </template>
    </Dialog>

    <!-- Custom Delete Confirmation Dialog -->
    <Dialog :draggable="false"
        v-model:visible="showDeleteDialog" 
        modal 
        header="Delete Contact" 
        :style="{ width: '25rem' }"
        :closable="true"
    >
        <div class="delete-confirmation-content">
            <div class="delete-icon">
                <i class="pi pi-exclamation-triangle"></i>
            </div>
            <p class="delete-message">Are you sure you want to delete this contact?</p>
        </div>
        
        <template #footer>
            <Button 
                label="No" 
                class="delete-cancel-button" 
                @click="cancelDelete"
            />
            <Button 
                label="Yes" 
                class="delete-confirm-button" 
                @click="confirmDelete"
            />
        </template>
    </Dialog>

    <!-- Bulk Partner Upload Dialog -->
    <Dialog :draggable="false" v-model:visible="showBulkUploadDialog" modal header="Bulk Upload" :style="{ width: '30rem' }">
            <div class="upload-container">
                <div v-if="isUploading" class="upload-loader">
                    <ProgressBar mode="indeterminate" style="height: 6px"></ProgressBar>
                    <p>Uploading and processing file...</p>
                </div>
                <div v-else-if="uploadResults" class="upload-results">
                    <!-- Success List -->
                    <div v-if="uploadResults.successful.length > 0" class="result-section">
                        <h4 class="success-header">Successfully Added</h4>
                        <ul class="result-list success-list">
                            <li v-for="(item, index) in uploadResults.successful" :key="index">
                                <div>
                                    <div>{{ item?.email }}</div>
                                    <div v-if="!item?.userSignupStatus?.success" class="warning-message">
                                        Email already exists. Added as vendor contract to existing vendor
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>

                    <!-- Failed List -->
                    <div v-if="uploadResults.failed.length > 0" class="result-section">
                        <h4 class="error-header">Failed to Add</h4>
                        <ul class="result-list error-list">
                            <li v-for="(item, index) in uploadResults.failed" :key="index">
                                {{ item.partner.user.email }} - {{ item.error }}
                            </li>
                        </ul>
                    </div>

                    <Button label="Close" class="p-button-primary mt-2 " @click="closeUploadDialog" />
                </div>
                <div v-else>
                    <Button label="Download Template and Instructions" class="p-button-primary remove-active-style" style="width: 100% !important; margin-bottom: 1rem;" @click="downloadTemplateAndInstructions" />
                    <FileUpload mode="basic" :customUpload="true" @uploader="uploadFile" accept=".xlsx" :auto="true"
                        chooseLabel="Choose Excel File" class="uploader" />
                    <small>Only .xlsx files are allowed</small>
                </div>
            </div>
    </Dialog>


    <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
    </div>
</template>

<script>
import AccountMangementService from '../../service/AccountManagementService';
import UserManagementService from '../../service/Usermanagement';
import optionsService from '../../service/AnswerOptionsService';
import { required, minLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { Checkbox } from 'primevue/checkbox';
import Dialog from 'primevue/dialog';
import FileUpload from 'primevue/fileupload';
import ProgressBar from 'primevue/progressbar';
import * as XLSX from 'xlsx';
import PartnerSearch from '../../components/PartnerSearch.vue';
import EmailDeliverabilityBadge from '../../components/EmailDeliverabilityBadge.vue';
import AiPrePopulationStep from '../../components/aiPrePopulation/AiPrePopulationStep.vue';
import VendorSourceService from '../../service/VendorSourceService';
import EventBus from '../../event-bus'
import {USER_ROLE_IDS} from '../../utilities/constants'

export default {
    components: {
        Checkbox,
        Dialog,
        FileUpload,
        ProgressBar,
        PartnerSearch,
        EmailDeliverabilityBadge,
        AiPrePopulationStep
    },
    data() {
        return {
            userDetails: null,
            // Mirrors AiPrePopulationStep's runInProgress/lastRun, kept in
            // sync via its run-state-changed emit -- reading these through
            // $refs in a computed is unreliable (only registers a reactive
            // dependency if the ref was already populated on that
            // computed's first evaluation).
            aiRunState: { runInProgress: false, lastRun: null },
            Name: '',
            quickbooksID: '',
            description: '',
            channels: [],
            adminFirstName: '',
            adminLastName: '',
            adminEmail: '',
            showBulkUploadDialog: false,
            isUploading: false,
            uploadResults: null,
            requiredColumns: [
            'vendor_name',
            'user_first_name',
            'user_last_name',
            'user_email',
            'commission_rate'
            ],
            loading: false,
            commissionRate: '',
            notes: '',
            selectedPartner: null,
            currentStep: 1,
            isNewVendor: false,
            showContactDialog: false,
            showDeleteDialog: false,
            showPrimaryContactReplaceDialog: false,
            deleteContactIndex: -1,
            contacts: [],
            // Email-suppression deep-link support. When the report or report-row Update
            // button navigates here with ?highlight=<email>, we outline the matching
            // contact row and scroll it into view.
            highlightEmail: null,
            highlightRowEl: null,
            contactForm: {
                name: '',
                title: '',
                email: '',
                phone: '',
                isPrimaryContact: false,
                isEscalationContact: false,
                isRFPContributor: false,
                isRFPInvitationRecipient: false
            },
            editingContactIndex: -1,
            vendorSharing: {
                channelVisibility: 'public' // 'public', 'private', 'select'
            },
            partnerDistributors: [], // Store distributor details for selected partner
            partnerDistributorId: null,
            // Feature flag — gates the entire AI Pre-Population step (Step 4).
            // When false the wizard behaves exactly like it did before the
            // AI feature landed: stepper omits the AI step, Step 3 (Vendor
            // Relationship) advances directly to Step 5 (Visibility), and
            // Step 5's Previous button returns directly to Step 3.
            // Read at build time from VUE_APP_AI_PREPOP_ENABLED — default off
            // until Stages 2/3 land and the Anthropic API key is provisioned.
            aiPrePopEnabled: process.env.VUE_APP_AI_PREPOP_ENABLED === 'true',
            // qsid for the SE Question Library — resolved at runtime; for
            // now read from env (see VUE_APP_SE_QUESTION_SET_ID). The AI
            // pre-population feature initially targets SE only.
            seQuestionSetId: process.env.VUE_APP_SE_QUESTION_SET_ID
                ? parseInt(process.env.VUE_APP_SE_QUESTION_SET_ID, 10)
                : 45,
            defaultSubnetworkId: process.env.VUE_APP_DEFAULT_SUBNETWORK_ID,
            excludedSubnetworkId: process.env.VUE_APP_EXCLUDED_SUBNETWORK_ID,
            platformName: process.env.VUE_APP_PLATFORM_NAME
        };
    },
    validations() {
        return {
            Name: { required, minLength: minLength(1) },
            quickbooksID: {
                minLength: minLength(1),
                numeric: (value) => {
                    if (!value) return true; // Let required validation handle empty values
                    return /^\d+$/.test(value.toString());
                }
            },
            description: { required, minLength: minLength(1) },
            adminFirstName: { required, minLength: minLength(1) },
            adminLastName: { required, minLength: minLength(1) },
            adminEmail: {
                required,
                minLength: minLength(1),
                email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            },
            commissionRate: {
                required,
                minValue: (value) => {
                    if (!value) return true;
                    const num = parseFloat(value);
                    return !isNaN(num) && num >= 0;
                }
            },
            notes: {
                required: (value) => {
                    // Notes are optional for all account types
                    return true;
                }
            }
        };
    },
    setup() {
        return { v$: useVuelidate() };
    },
    computed: {
        currentUserSubnetworkId() {
        return this.isChannel ? this.subnetworkIdOfOtherChannels : this.defaultSubnetworkId;
            },

        // Mirrors AiPrePopulationStep's runInProgress (see aiRunState /
        // onAiRunStateChanged) -- guards Skip/Finish so the one-shot
        // welcome email can't fire mid-run (see the ai-prepop-nav-footer
        // template comment).
        isAiRunInProgress() {
            return !!this.aiRunState.runInProgress;
        },

        // True once a run has started (in progress, completed, or failed) --
        // hides the "Skip this step" button, since its result now decides
        // the welcome email and there's nothing left to "skip".
        hasAiRunStarted() {
            return !!this.aiRunState.lastRun || this.isAiRunInProgress;
        },
            
        isStep2Valid() {
            // Check if all required fields in Step 2 are filled
            const nameValid = this.Name && this.Name.trim().length > 0;
            const descriptionValid = this.description && this.description.trim().length > 0;
            
            console.log('Validation check:', {
                name: nameValid,
                description: descriptionValid
            });
            
            return nameValid && descriptionValid;
        },
        
        isStep3Valid() {
            // Check if at least one contact is present and at least one primary contact exists
            if (!this.contacts || this.contacts.length === 0) {
                return false;
            }
            
            // Check if at least one contact is marked as primary
            if (!this.hasPrimaryContact) {
                return false;
            }
            
            // Check if commission rate is provided and valid
            if (!this.commissionRate || this.commissionRate.toString().trim() === '') {
                return false;
            }
            
            const commissionRate = parseFloat(this.commissionRate);
            if (isNaN(commissionRate) || commissionRate < 0) {
                return false;
            }
            
            // Check if admin fields are populated from primary contact
            if (!this.adminFirstName || !this.adminEmail) {
                return false;
            }
            
            return true;
        },
        
        // Step labels for the new-vendor flow. Visibility Settings comes
        // before AI Pre-Population so the vendor account (and its account_id)
        // exists by the time pre-population runs. AI Pre-Population is
        // inserted only when the feature flag is on.
        partnerSteps() {
            const steps = [
                'Search for Existing Vendor',
                'Create New Vendor',
                'Vendor Relationship',
                'Visibility Settings',
            ];
            if (this.aiPrePopEnabled) steps.push('AI Pre-Population');
            return steps;
        },
        // Step labels for the existing-vendor flow. Same rule as above.
        existingVendorSteps() {
            const steps = [
                'Search for Existing Vendor',
                'Vendor Relationship',
                'Visibility Settings',
            ];
            if (this.aiPrePopEnabled) steps.push('AI Pre-Population');
            return steps;
        },

        currentStepperSteps() {
            // Return different steps based on vendor type
            return this.isNewVendor ? this.partnerSteps : this.existingVendorSteps;
        },
        
        totalSteps() {
            return this.currentStepperSteps.length;
        },
        
        adjustedCurrentStep() {
            // For existing vendor flow, adjust step numbers (Step 2 = Create
            // New Vendor is skipped). AI Pre-Population (currentStep 5) is
            // now the LAST step in both flows, so it only exists as a
            // stepper slot when the feature flag is on -- but since nothing
            // ever advances currentStep to 5 while the flag is off, no
            // special-casing is needed for that case (unlike the old
            // middle-of-flow AI Pre-Pop position, which required skipping
            // a hole).
            if (!this.isNewVendor) {
                if (this.currentStep === 1) return 1; // Search
                if (this.currentStep === 3) return 2; // Vendor Relationship
                if (this.currentStep === 4) return 3; // Visibility Settings
                if (this.currentStep === 5) return 4; // AI Pre-Population (last)
                return this.currentStep;
            }
            // New vendor flow — currentStep already matches the stepper slot
            // 1:1 (Search, Create, Vendor Relationship, Visibility, AI Pre-Pop).
            return this.currentStep;
        },
        
        hasPrimaryContact() {
            // Check if any existing contact is marked as primary
            return this.contacts.some(contact => contact.isPrimaryContact);
        },
        
        isNameFieldDisabled() {
            // Disable the Name field if we're in Step 2 and the Name is populated from search
            return this.currentStep === 2 && this.isNewVendor && this.Name && this.Name.trim().length > 0;
        },

    },
    created() {
        this.accountMangementService = new AccountMangementService();
        this.fetchChannels(); // Call the fetch method on creation
        this.userDetails = JSON.parse(localStorage.getItem('pro_marketplaceUser'));
        this.isChannel = this.userDetails.value.userRole.roleKey === "channel:business:admin";
        this.userManagementService = new UserManagementService();
        this.vendorSourceService = new VendorSourceService();
        this.fetchIndustryOptions();
        this.subnetworkIdOfOtherChannels = this.isChannel ? this.userDetails.value.subnetworkId : null;
        // Pick up the suppression-report deep-link target (case-insensitive match).
        const h = this.$route?.query?.highlight;
        if (h && typeof h === 'string') {
            this.highlightEmail = h.toLowerCase();
        }
    },
    updated() {
        // After contacts render, scroll the highlighted row into view once.
        if (this.highlightEmail && this.highlightRowEl) {
            this.$nextTick(() => {
                try {
                    this.highlightRowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } catch (_) { /* old browser, no-op */ }
                this.highlightRowEl = null; // only scroll once per visit
            });
        }
    },
    watch: {
        accountType(newType, oldType) {
            if (newType !== oldType) {
                this.currentStep = 1;
                this.selectedPartner = null;
                this.isNewVendor = false;
                this.resetForm();
            }
        },
        'vendorSharing.channelVisibility'(newVal) {
            if (newVal === 'select') {
                // Auto-select current user's channel when visibility changes to 'select'
                const currentUserChannel = this.channels.find(
                    channel => channel.subnetwork_id === this.currentUserSubnetworkId
                );
                if (currentUserChannel) {
                    currentUserChannel.selectedForSharing = true;
                }
            }
    }
    },
    methods: {

        isHighlighted(contact) {
            if (!this.highlightEmail || !contact?.email) return false;
            return contact.email.toLowerCase() === this.highlightEmail;
        },

        async downloadTemplateAndInstructions() {
            try {
                // Get the presigned URL for the template from the backend
                const response = await this.accountMangementService.getBulkUploadTemplate();

                if (response.status === 200 && response.data.downloadURL) {
                    // Get the signed URL from the response
                    const signedUrl = response.data.downloadURL;
                    const fileName = response.data.fileName || 'Bulk_Vendor_Upload_Template.xlsx';

                    // Fetch the file as blob
                    const fileResponse = await fetch(signedUrl);
                    const blob = await fileResponse.blob();

                    // Create object URL
                    const url = window.URL.createObjectURL(blob);

                    // Create a temporary link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;

                    // Append to body, click and remove
                    document.body.appendChild(link);
                    link.click();

                    // Clean up
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Download Started',
                        detail: 'Template download has started.',
                        life: 3000
                    });
                } else {
                    throw new Error('Failed to get download URL');
                }
            } catch (error) {
                console.error('Error downloading template:', error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Download Failed',
                    detail: 'An error occurred while downloading the template. Please try again.',
                    life: 3000
                });
            }
        },


        async checkTemplate(ch) {
            ch.isContractTemplate = (
                await this.accountMangementService.getContractTemplate(ch.subnetwork_id)
            ).data.data.templateExists;
        },
        toggleChannel(ch) { },

        async fetchIndustryOptions() {
            const optionsSvc = new optionsService();
            try {
                const optionData = await optionsSvc.getOptions();
                console.log("Raw Option Data -->", optionData); // Log raw data

                let industryData = {};
                if (optionData.status === 200 && Array.isArray(optionData.data.data)) {
                    optionData.data.data.forEach(option => {
                        if (option.is_public) { // Check if is_public is true
                            if (!industryData[option.industry_name]) {
                                industryData[option.industry_name] = [];
                            }
                            industryData[option.industry_name].push({
                                specialty: option.specialty_name,
                                id: option.ref_industry_id
                            });
                        }
                    });
                }

                console.log("Industry Data -->", industryData);

                // Remove duplicates
                for (const industry in industryData) {
                    industryData[industry] = this.removeDuplicateSpecialties(industryData[industry]);
                }

                console.log("Processed Industry Data -->", industryData); // Log processed data
                this.industryOptions = this.transformIndustryData(industryData);
            } catch (error) {
                console.error('Error fetching industries:', error);
            }
        },
        removeDuplicateSpecialties(specialties) {
            const uniqueSpecialties = [];
            const seenSpecialties = new Set();

            specialties.forEach(specialty => {
                if (!seenSpecialties.has(specialty.specialty)) {
                    uniqueSpecialties.push(specialty);
                    seenSpecialties.add(specialty.specialty);
                }
            });

            return uniqueSpecialties;
        },
        transformIndustryData(data) {
            if (!data) {
                return [];
            }
            console.log("Data -->", data);
            const industryOptions = Object.keys(data).map(industryName => ({
                label: industryName,
                items: data[industryName].map(specialty => ({
                    label: specialty.specialty,
                    value: specialty.id // Assuming specialty name is unique and can be used as a value
                }))
            }));


            return industryOptions;
        },
        async fetchChannels() {
            try {
                const response = await this.accountMangementService.getChannels();
                if (response.status === 200) {
                    const defaultSubnetworkId = this.defaultSubnetworkId;
                    this.channels = response?.data.data
                        .map((channel) => {
                            return { ...channel, enabled: true, selectedTemplate: 'default', selectedForSharing: false };
                        })
                        .sort((a, b) => {
                            if (a.subnetwork_id === defaultSubnetworkId) {
                                return -1;
                            }
                            return 1;
                        });
                } else {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error fetching channels',
                        detail: response.data,
                        life: 3000,
                    });
                }
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error fetching channels',
                    detail: error.message,
                    life: 3000,
                });
            }
        },

        submit() {
            this.v$.$touch();
            if (!this.v$.$error) {
                this.addUser();
            }
        },

        async addUser() {
            this.loading = true;
            var payload = {
                quickbooksID: this.quickbooksID,
                name: this.Name,
                description: this.description,
                adminFirstName: this.adminFirstName,
                adminLastName: this.adminLastName,
                adminEmail: this.adminEmail,
            };
            
            // If this is a partner selected from search, include the existing partner ID
            if (this.selectedPartner) {
                payload.existingPartnerId = this.selectedPartner.id;
            }
            
            payload.isPartner = true;
            payload.accreditedChannels = this.isAccredited ? [
                'f4cd7264-3f00-463b-b2e9-d9e69c3ec193'
            ] : [];
            payload.channels = this.isChannel ? [{
                subnetworkId: 'f4cd7264-3f00-463b-b2e9-d9e69c3ec193',
                contractSubnetworkId: 'f4cd7264-3f00-463b-b2e9-d9e69c3ec193'
            }, {
                subnetworkId: this.userDetails.value.subnetworkId,
                contractSubnetworkId: "f4cd7264-3f00-463b-b2e9-d9e69c3ec193"
            }] : this.channels
                .filter((ch) => ch.enabled)
                .map((channel) => {
                    return {
                        subnetworkId: channel.subnetwork_id,
                        contractSubnetworkId:
                            channel.selectedTemplate === 'default'
                                ? 'f4cd7264-3f00-463b-b2e9-d9e69c3ec193'
                                : channel.subnetwork_id,
                    };
                })
            payload.commission_rate = this.commissionRate;
            payload.notes = this.notes;

            var userResponse = await this.accountMangementService.addNewUser(payload);
            console.log(userResponse);
            if (userResponse.status == 200) {
                let userCreationPayload = {
                    email: this.adminEmail,
                    account_id: userResponse.data.accountID,
                    user_role_id: USER_ROLE_IDS.PARTNER,
                    last_name: this.adminLastName,
                    first_name: this.adminFirstName,
                }
                await this.userManagementService.addNewUser(userCreationPayload);
                this.$toast.add({ severity: 'success', summary: `Vendor Account Created`, life: 3000 });
                this.resetForm();
                await this.fetchChannels();
                this.currentStep = 1;
                this.selectedPartner = null;
                (this.channels = this.channels.map((channel) => {
                    channel.enabled = true;
                    channel.selectedTemplate = 'default';
                    return channel;
                }));
                // Refresh the menu data to remove the deleted channel
                EventBus.emit('refresh-menu');
                this.loading = false;
                return;
            }
            this.$toast.add({
                severity: 'error',
                summary: 'Error while creating user',
                detail: userResponse.data,
                life: 3000,
            });
            this.loading = false;
        },
        openBulkUploadDialog() {
            this.showBulkUploadDialog = true;
        },

        async uploadFile(event) {
            this.loading = true;
            try {
                console.log("Uploading File -->", event);
                const file = event.files[0];
                // Validate Excel content
                const validationResult = await this.validateExcelContent(file);
                console.log("Validation Result -->", validationResult);
                if (!validationResult.isValid) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Invalid Excel Format',
                        detail: validationResult.error,
                        life: 5000,
                    });
                    return;
                }

                this.isUploading = true;
                this.uploadResults = null; // Reset results

                // Show loading state
                this.$toast.add({
                    severity: 'success',
                    summary: 'Upload Started',
                    detail: 'Uploading file...',
                    life: 3000,
                });

                // Step 1: Get presigned URL
                const presignedUrlResponse = await this.accountMangementService.getPresignedUrl(file.name, file.type);
                const { uploadURL, Key } = presignedUrlResponse.data;

                // Step 2: Upload to S3
                await this.accountMangementService.uploadToS3(uploadURL, file);

                const payloadForBulkUpload = {
                    key: Key,
                    subnetworkId: this.userDetails.value.subnetworkId ? this.userDetails.value.subnetworkId : null
                }

                // Step 3: Trigger bulk upload processing
                const bulkUploadResponse = await this.accountMangementService.triggerBulkUpload(payloadForBulkUpload);
                console.log(bulkUploadResponse);

                // Store the results
                this.uploadResults = bulkUploadResponse.data;

                if (bulkUploadResponse?.data?.failed?.length > 0) {
                    // Don't throw error, let's show the results instead
                    return;
                }

                // Only close dialog if there were no failures
                setTimeout(() => {
                    this.closeUploadDialog();
                }, 3000);

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Upload Failed',
                    detail: error.message || 'Error processing bulk upload',
                    life: 3000,
                });
            } finally {
                this.isUploading = false;
                this.loading = false;
            }
        },

        closeUploadDialog() {
            this.showBulkUploadDialog = false;
            this.uploadResults = null;
        },

        async validateExcelContent(file) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                console.log("Reader -->", reader);

                reader.onload = (e) => {
                    try {
                        const data = new Uint8Array(e.target.result);
                        console.log("Data -->", data);
                        const workbook = XLSX.read(data, { type: 'array' });
                        console.log("Workbook -->", workbook);

                        // Get the first sheet
                        const firstSheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[firstSheetName];
                        console.log("Worksheet -->", firstSheetName);

                        // Convert to JSON
                        const jsonData = XLSX.utils.sheet_to_json(worksheet);

                        if (jsonData.length === 0) {
                            resolve({ isValid: false, error: 'Excel file is empty' });
                            return;
                        }

                        // Get headers from the first row
                        const headers = Object.keys(jsonData[0]);

                        // Check if all required columns exist
                        const missingColumns = this.requiredColumns.filter(
                            col => !headers.includes(col)
                        );

                        if (missingColumns.length > 0) {
                            resolve({
                                isValid: false,
                                error: `Missing required columns: ${missingColumns.join(', ')}`
                            });
                            return;
                        }

                        // Check each row for empty values
                        for (let i = 0; i < jsonData.length; i++) {
                            const row = jsonData[i];
                            const rowNumber = i + 2; // Adding 2 because Excel rows start at 1 and we have headers

                            // Check each required column
                            for (const column of this.requiredColumns) {
                                const value = row[column];

                                // Check if value is empty, undefined, null, or just whitespace
                                if (value === undefined || value === null ||
                                    (typeof value === 'string' && value.trim() === '') ||
                                    value === '') {
                                    resolve({
                                        isValid: false,
                                        error: `Empty value found in column "${column}" at row ${rowNumber}`
                                    });
                                    return;
                                }

                                // Special validation for email
                                if (column === 'user_email' && !this.isValidEmail(value)) {
                                    resolve({
                                        isValid: false,
                                        error: `Invalid email format in row ${rowNumber}: ${value}`
                                    });
                                    return;
                                }

                                // Special validation for commission rate
                                if (column === 'commission_rate' && !this.isValidCommissionRate(value)) {
                                    resolve({
                                        isValid: false,
                                        error: `Invalid commission rate in row ${rowNumber}: ${value}. Commission rate must contain only numbers.`
                                    });
                                    return;
                                }
                            }
                        }

                        // If we get here, all validations passed
                        resolve({ isValid: true });

                    } catch (error) {
                        resolve({
                            isValid: false,
                            error: 'Error reading Excel file. Please make sure it\'s a valid Excel file.'
                        });
                    }
                };

                reader.readAsArrayBuffer(file);
            });
        },


        // Email validation
        isValidEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
        },

        // Commission rate validation
        isValidCommissionRate(value) {
            // Check if value is a valid number (including decimal numbers)
            const numValue = parseFloat(value);
            return !isNaN(numValue) && isFinite(numValue) && numValue >= 0;
        },

        
        async onPartnerSelected(partner) {
            console.log("Selected Partner -->", partner);
            this.selectedPartner = partner;
            this.currentStep = 3; // Skip to Step 3 for existing partner
            this.isNewVendor = false; // Existing vendor selected
            
            // Pre-fill form with selected partner data
            this.Name = partner.name;
            this.quickbooksID = partner.quickbooksID || '';
            this.description = partner.description || '';
            this.adminFirstName = partner.adminFirstName || '';
            this.adminLastName = partner.adminLastName || '';
            this.adminEmail = partner.adminEmail || '';
            
            // Fetch distributor details for the selected partner
            try {
                const distributorResponse = await this.accountMangementService.getPartnerDistributors(partner.account_id);
                if (distributorResponse.status === 200 && distributorResponse.data) {
                    this.partnerDistributors = distributorResponse.data.distributor ? distributorResponse.data.distributor : [];
                    this.partnerDistributorId = distributorResponse.data.distributor_id;
                    this.commissionRate = this.partnerDistributors.commission_rate ? 
                        this.partnerDistributors.commission_rate.toString().replace('%', '') : '';
                    this.notes = this.partnerDistributors.notes;
                    if (this.partnerDistributors.contacts && this.partnerDistributors.contacts.length > 0) {
                        this.contacts = this.partnerDistributors.contacts.map(contact => ({
                            name: contact.name || '',
                            title: contact.title || '',
                            email: contact.email || '',
                            phone: contact.phone || '',
                            isPrimaryContact: contact.isPrimary || false,
                            isEscalationContact: contact.isEscalation || false,
                            isRFPContributor: contact.isRFPContributor || false,
                            isRFPInvitationRecipient: contact.isRFPInvitationRecipient || false,
                            // Deliverability fields populated by the augmented get_partner_distributors
                            // Lambda. Drives the EmailDeliverabilityBadge next to the email cell.
                            deliverability_status: contact.deliverability_status || 'OK',
                            last_bounced_at: contact.last_bounced_at || null,
                            last_diagnostic: contact.last_diagnostic || null
                        }));
                        
                        // Auto-populate admin fields from primary contact after loading existing vendor data
                        this.populateAdminFieldsFromPrimaryContact();
                    }
                } else {
                    console.warn('No distributor data found for partner:', partner.name);
                    this.partnerDistributors = [];
                    this.commissionRate = '';
                    this.notes = '';
                    this.contacts = [];
                    this.partnerDistributorId = null;
                }
            } catch (error) {
                console.error('Error fetching partner distributors:', error);
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Warning',
                    detail: 'Could not fetch distributor details for the selected partner',
                    life: 3000,
                });
                this.partnerDistributors = [];
            }
            
            this.$toast.add({
                severity: 'success',
                summary: 'Vendor Selected',
                detail: `Selected existing vendor: ${partner.name}`,
                life: 3000,
            });
        },
        
        onProceedToCreate(vendorName) {
            this.selectedPartner = null;
            this.currentStep = 2; // Go to Step 2 for creating new partner
            this.isNewVendor = true; // New vendor creation flow
            
            // Populate the Name field with the searched vendor name
            if (vendorName && vendorName.trim()) {
                this.Name = vendorName.trim();
            }
            
            this.$toast.add({
                severity: 'success',
                summary: 'Creating New Vendor',
                detail: 'Proceeding to create a new vendor account',
                life: 3000,
            });
        },
        
        backToSearch() {
            this.currentStep = 1;
            this.selectedPartner = null;
            this.isNewVendor = false;
            this.resetForm();
        },

        goToPreviousStep() {
            if (this.currentStep > 1) {
                // Handle different flows based on isNewVendor flag
                if (this.currentStep === 3 && !this.isNewVendor) {
                    // Existing vendor selected: Step 3 → Step 1 (Search)
                    this.currentStep = 1;
                    this.selectedPartner = null;
                    this.isNewVendor = false;
                    this.resetForm();
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Step 1: Search for Existing Vendor',
                        detail: 'Back to vendor search',
                        life: 3000,
                    });
                } else {
                    // Normal step decrement. AI Pre-Population (step 5) is
                    // now the last step in both flows, so there's no longer
                    // a hole to skip when the feature flag is off -- with it
                    // off, currentStep never advances to 5 in the first
                    // place, so a plain decrement always lands correctly.
                    this.currentStep--;
                    this.$toast.add({
                        severity: 'success',
                        summary: this.getStepTitle(this.currentStep),
                        detail: 'Navigating to previous step',
                        life: 3000,
                    });
                }
            }
        },

        getBackButtonLabel() {
            // Handle different flows based on isNewVendor flag
            if (this.currentStep === 3 && !this.isNewVendor) {
                // Existing vendor selected: Step 3 → Step 1 (Search)
                return 'Search';
            } else if (this.currentStep === 3 && this.isNewVendor) {
                // New vendor flow: Step 3 → Step 2 (Create Vendor)
                return 'Create Vendor';
            } else if (this.currentStep === 2) {
                // Step 2 → Step 1 (Search)
                return 'Search';
            } else if (this.currentStep === 4) {
                // Step 4 (Visibility Settings) → Step 3 (Vendor Relationship)
                return 'Vendor Relationship';
            } else if (this.currentStep === 5) {
                // Step 5 (AI Pre-Pop, last step, flag-gated) → Step 4
                // (Visibility Settings). Only reachable when the flag is on.
                return 'Visibility Settings';
            }
            return 'Previous';
        },
        
        resetForm() {
            this.Name = '';
            this.quickbooksID = '';
            this.description = '';
            this.adminFirstName = '';
            this.adminLastName = '';
            this.adminEmail = '';
            this.commissionRate = '';
            this.notes = '';
            this.isAccredited = false;
            this.partnerDistributors = []; // Reset partner distributors
            this.partnerDistributorId = null;
            this.contacts = [];
            this.v$.$reset();
            
            // Ensure admin fields are cleared when form is reset
            this.populateAdminFieldsFromPrimaryContact();
        },

        // Contact Management Methods
        openContactDialog() {
            this.showContactDialog = true;
            this.resetContactForm();
        },

        closeContactDialog() {
            this.showContactDialog = false;
            this.resetContactForm();
            this.editingContactIndex = -1;
        },

        resetContactForm() {
            console.log("Resetting Contact Form: ", this.hasPrimaryContact);
            this.contactForm = {
                name: '',
                title: '',
                email: '',
                phone: '',
                isPrimaryContact: !this.hasPrimaryContact, // Set to true only if no primary contact exists
                isEscalationContact: false,
                isRFPContributor: !this.hasPrimaryContact,
                isRFPInvitationRecipient: !this.hasPrimaryContact, // Auto-select if this will be primary contact
            };
        },

        saveContact() {
            // Name validation
            if (!this.contactForm.name.trim()) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Name is required',
                    life: 3000,
                });
                return;
            }

            // Title validation
            if (!this.contactForm.title.trim()) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Title is required',
                    life: 3000,
                });
                return;
            }

            // Email validation
            if (!this.contactForm.email.trim()) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Email is required',
                    life: 3000,
                });
                return;
            }

            // Email format validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.contactForm.email.trim())) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Enter a valid email address',
                    life: 3000,
                });
                return;
            }

            // Phone validation
            if (!this.contactForm.phone.trim()) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Phone number is required',
                    life: 3000,
                });
                return;
            }

            // Phone format validation
            const phoneRegex = /^(\+\d{1,3}[\s-]?)?\(?\d{3}\)?[\s-]?\d{3}[\s-]?\d{4}$|^(\+\d{1,3}[\s-]?)?\d{10,15}$/;
            if (!phoneRegex.test(this.contactForm.phone.trim())) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Enter a valid phone number',
                    life: 3000,
                });
                return;
            }



            // Primary contact validation - only one primary contact allowed per vendor
            if (!this.contactForm.isPrimaryContact && !this.hasPrimaryContact) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Primary Contact Required',
                    detail: 'At least one Primary Contact is required',
                    life: 3000,
                });
                return;
            }

            // Ensure RFP Invitation Recipient is selected if Primary Contact is selected
            if (this.contactForm.isPrimaryContact && !this.contactForm.isRFPInvitationRecipient) {
                this.contactForm.isRFPInvitationRecipient = true;
            }

            // Email uniqueness validation
                const normalizedNewEmail = this.contactForm.email.trim().toLowerCase();
                const isDuplicate = this.contacts.some((contact, index) => {
                    // Skip the current contact if we're editing
                    if (this.editingContactIndex === index) return false;
                    
                    return contact.email && contact.email.trim().toLowerCase() === normalizedNewEmail;
                });

                if (isDuplicate) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Duplicate Email',
                        detail: 'This email address is already used by another contact',
                        life: 3000,
                    });
                    return;
                }

            // All validations passed - save the contact
            if (this.editingContactIndex >= 0) {
                // Update existing contact
                // If this contact is being set as primary, remove primary from all other contacts
                if (this.contactForm.isPrimaryContact) {
                    this.contacts.forEach((contact, idx) => {
                        if (idx !== this.editingContactIndex) {
                            contact.isPrimaryContact = false;
                        }
                    });
                }
                this.contacts[this.editingContactIndex] = { ...this.contactForm };
                this.$toast.add({
                    severity: 'success',
                    summary: 'Contact Updated',
                    detail: 'Contact information has been updated successfully',
                    life: 3000,
                });
            } else {
                // Add new contact
                // If this contact is being set as primary, remove primary from all other contacts
                if (this.contactForm.isPrimaryContact) {
                    this.contacts.forEach(contact => {
                        contact.isPrimaryContact = false;
                    });
                }
                this.contacts.push({ ...this.contactForm });
                this.$toast.add({
                    severity: 'success',
                    summary: 'Contact Added',
                    detail: 'New contact has been added successfully',
                    life: 3000,
                });
            }

            // Auto-populate admin fields after saving contact
            this.populateAdminFieldsFromPrimaryContact();

            this.closeContactDialog();
        },

        editContact(index) {
            this.editingContactIndex = index;
            this.contactForm = { ...this.contacts[index] };
            this.showContactDialog = true;
        },

        deleteContact(index) {
            this.deleteContactIndex = index;
            this.showDeleteDialog = true;
        },

        confirmDelete() {
            if (this.deleteContactIndex >= 0) {
                this.contacts.splice(this.deleteContactIndex, 1);
                this.$toast.add({
                    severity: 'success',
                    summary: 'Contact Deleted',
                    detail: 'Contact has been deleted successfully',
                    life: 3000,
                });
                
                // Update admin fields after deleting contact
                this.populateAdminFieldsFromPrimaryContact();
            }
            this.cancelDelete();
        },

        cancelDelete() {
            this.showDeleteDialog = false;
            this.deleteContactIndex = -1;
        },

        cancelPrimaryContactReplace() {
            this.showPrimaryContactReplaceDialog = false;
            // Uncheck the primary contact checkbox since user declined to replace
            this.contactForm.isPrimaryContact = false;
        },

        confirmPrimaryContactReplace() {
            this.showPrimaryContactReplaceDialog = false;
            
            // Find existing primary contact and convert to RFP Contributor
            const existingPrimaryIndex = this.contacts.findIndex(contact => contact.isPrimaryContact);
            if (existingPrimaryIndex !== -1) {
                this.contacts[existingPrimaryIndex].isPrimaryContact = false;
                this.contacts[existingPrimaryIndex].isRFPContributor = true;
            }
            
            // Set current contact as primary
            this.contacts.forEach(contact => {
                contact.isPrimaryContact = false;
            });
            
            // Set the current contact form as primary (use $nextTick to avoid triggering change event)
            this.$nextTick(() => {
                this.contactForm.isPrimaryContact = true;
                
                // Auto-select RFP Contributor and RFP Invitation Recipient when Primary Contact is selected
                this.contactForm.isRFPContributor = true;
                this.contactForm.isRFPInvitationRecipient = true;
                
                // Auto-populate admin fields when primary contact changes
                this.populateAdminFieldsFromPrimaryContact();
            });
        },

        deleteExistingPrimaryContact() {
            this.showPrimaryContactReplaceDialog = false;
            
            // Check if this is the last contact and we're not currently adding a new one (only editing)
            // If editing the same contact index, we need at least 1 contact
            // If adding new, deletion is okay since we'll add the new one
            const isAddingNew = this.editingContactIndex < 0;
            
            if (this.contacts.length <= 1 && !isAddingNew) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Cannot Delete Contact',
                    detail: 'At least one contact must remain. Please add the new contact first.',
                    life: 3000,
                });
                // Reset the primary checkbox since deletion was prevented
                this.contactForm.isPrimaryContact = false;
                return;
            }
            
            // Find and delete the existing primary contact
            const existingPrimaryIndex = this.contacts.findIndex(contact => contact.isPrimaryContact);
            if (existingPrimaryIndex !== -1) {
                const deletedContactName = this.contacts[existingPrimaryIndex].name;
                this.contacts.splice(existingPrimaryIndex, 1);
                
                this.$toast.add({
                    severity: 'success',
                    summary: 'Contact Deleted',
                    detail: `${deletedContactName} has been deleted successfully`,
                    life: 3000,
                });
                
                // Adjust editingContactIndex if necessary
                if (this.editingContactIndex > existingPrimaryIndex) {
                    this.editingContactIndex--;
                } else if (this.editingContactIndex === existingPrimaryIndex) {
                    this.editingContactIndex = -1;
                }
            }
            
            // Set the current contact form as primary (use $nextTick to avoid triggering change event)
            this.$nextTick(() => {
                this.contactForm.isPrimaryContact = true;
                
                // Auto-select RFP Contributor and RFP Invitation Recipient when Primary Contact is selected
                this.contactForm.isRFPContributor = true;
                this.contactForm.isRFPInvitationRecipient = true;
                
                // Auto-populate admin fields when primary contact changes
                this.populateAdminFieldsFromPrimaryContact();
            });
        },

        handlePrimaryContactChange() {
            // If this contact is being set as primary
            if (this.contactForm.isPrimaryContact) {
                // Check if there's already a primary contact (and this isn't editing the existing primary)
                const existingPrimaryIndex = this.contacts.findIndex(contact => contact.isPrimaryContact);
                const isEditingExistingPrimary = this.editingContactIndex === existingPrimaryIndex;
                
                if (existingPrimaryIndex !== -1 && !isEditingExistingPrimary) {
                    // Show confirmation dialog to replace existing primary contact
                    this.showPrimaryContactReplaceDialog = true;
                    return; // Don't proceed until user confirms
                }
                
                // If confirmed or no existing primary, proceed with setting as primary
                this.contacts.forEach(contact => {
                    contact.isPrimaryContact = false;
                });
                
                // Auto-select RFP Contributor and RFP Invitation Recipient when Primary Contact is selected
                this.contactForm.isRFPContributor = true;
                this.contactForm.isRFPInvitationRecipient = true;
            }
            
            // Auto-populate admin fields when primary contact changes
            this.populateAdminFieldsFromPrimaryContact();
        },

        populateAdminFieldsFromPrimaryContact() {
            // Find the primary contact
            const primaryContact = this.contacts.find(contact => contact.isPrimaryContact);
            
            if (primaryContact) {
                // Split the name into first and last name
                const nameParts = primaryContact.name.trim().split(' ');
                const firstName = nameParts[0] || '';
                const lastName = nameParts[1] || '';

                // Update the admin fields
                this.adminFirstName = firstName;
                this.adminLastName = lastName;
                this.adminEmail = primaryContact.email;
                
                console.log('Admin fields populated from primary contact:', {
                    firstName,
                    lastName,
                    email: primaryContact.email
                });
            } else {
                // Clear admin fields if no primary contact exists
                this.adminFirstName = '';
                this.adminLastName = '';
                this.adminEmail = '';
                
                console.log('Admin fields cleared - no primary contact found');
            }
        },

        handleRFPContributorChange() {
            // If trying to deselect RFP Contributor while Primary Contact is selected, prevent it
            if (!this.contactForm.isRFPContributor && this.contactForm.isPrimaryContact) {
                this.contactForm.isRFPContributor = true; // Re-enable RFP Contributor
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Cannot Deselect RFP Contributor',
                    detail: 'Primary Contact must be deselected first before deselecting RFP Contributor',
                    life: 3000,
                });
            }
            
            // If RFP Contributor is deselected, also deselect RFP Invitation Recipient
            if (!this.contactForm.isRFPContributor && !this.contactForm.isPrimaryContact) {
                this.contactForm.isRFPInvitationRecipient = false;
            }
        },

        handleRFPInvitationRecipientChange() {
            // If trying to deselect RFP Invitation Recipient while Primary Contact is selected, prevent it
            if (!this.contactForm.isRFPInvitationRecipient && this.contactForm.isPrimaryContact) {
                this.contactForm.isRFPInvitationRecipient = true; // Re-enable RFP Invitation Recipient
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Cannot Deselect RFP Invitation Recipient',
                    detail: 'Primary Contact must be deselected first before deselecting RFP Invitation Recipient',
                    life: 3000,
                });
                return;
            }
            
            // RFP Invitation Recipient can only be checked if Primary Contact and/or RFP Contributor is checked
            if (this.contactForm.isRFPInvitationRecipient) {
                if (!this.contactForm.isPrimaryContact && !this.contactForm.isRFPContributor) {
                    this.contactForm.isRFPInvitationRecipient = false; // Uncheck it
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Cannot Select RFP Invitation Recipient',
                        detail: 'RFP Invitation Recipient can only be selected if Primary Contact and/or RFP Contributor is selected',
                        life: 3000,
                    });
                }
            }
        },

        validateNumberInput(event) {
            // Allow numeric input (0-9) and decimal point
            const char = String.fromCharCode(event.keyCode || event.which);
            const currentValue = event.target.value;
            
            // Allow digits, decimal point, and backspace/delete
            if (!/^[\d.]$/.test(char)) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Only numbers and decimal point are allowed',
                    life: 3000,
                });
                event.preventDefault();
                this.v$.quickbooksID.$touch();
                return false;
            }
            
            // Prevent multiple decimal points
            if (char === '.' && currentValue.includes('.')) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'Only one decimal point is allowed',
                    life: 3000,
                });
                event.preventDefault();
                return false;
            }
        },

        // Step Navigation Methods
       async saveVendorRelationship() {
            // Only proceed if Step 3 validation passes
            if (this.isStep3Valid) {
                const payload = this.buildExistingVendorPayload();
                if (!payload) {
                    return;
                }

                console.log('------>Saving existing vendor relationship with payload:', payload);

                // Find the primary contact to populate admin fields
                const primaryContact = this.contacts.find(contact => contact.isPrimaryContact);
                if (!primaryContact) {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Primary Contact Required',
                        detail: 'Please add at least one contact and mark it as primary before proceeding.',
                        life: 5000,
                    });
                    return null;
                }

                this.loading = true;

                const existingVendorResponse = await this.accountMangementService.addExistingPartner(payload);


                if (existingVendorResponse.status == 200) {
                    // Create user for Primary Contact (PARTNER role)
                    let userCreationPayload = {
                    email: primaryContact.email,
                    account_id: this.selectedPartner.account_id,
                    user_role_id: USER_ROLE_IDS.PARTNER,
                    last_name: primaryContact.name.split(' ').slice(1).join(' ') || '',
                    first_name: primaryContact.name.split(' ')[0] || '',
                    }
                    console.log('------>User creation payload for Existing Partner:', userCreationPayload);
                    await this.userManagementService.addNewUser(userCreationPayload);

                    // Create users for all RFP Contributors (excluding Primary Contact)
                    const rfpContributors = this.contacts.filter(contact => 
                        contact.isRFPContributor && !contact.isPrimaryContact
                    );
                    
                    for (const rfpContributor of rfpContributors) {
                        const rfpUserPayload = {
                            email: rfpContributor.email,
                            account_id: this.selectedPartner.account_id,
                            user_role_id: USER_ROLE_IDS.CONTRIBUTOR, // RFP Contributor role
                            last_name: rfpContributor.name.split(' ').slice(1).join(' ') || '',
                            first_name: rfpContributor.name.split(' ')[0] || '',
                        };
                        console.log('------>User creation payload for RFP Contributor:', rfpUserPayload);
                        await this.userManagementService.addNewUser(rfpUserPayload);
                    }

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Vendor Created Successfully',
                        detail: 'Vendor created successfully',
                        life: 3000,
                    });

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Vendor Relationship Updated',
                        detail: 'Vendor relationship updated successfully',
                        life: 3000,
                    });

                    if (this.aiPrePopEnabled) {
                        // Advance into AI Pre-Population (now the last
                        // step) instead of resetting the wizard.
                        // selectedPartner.account_id is already set (from
                        // the step-1 search), so AiPrePopulationStep can
                        // render immediately.
                        this.currentStep = 5;
                        this.$toast.add({
                            severity: 'success',
                            summary: this.getStepTitle(5),
                            detail: 'Optional — provide source material to pre-populate the Answer Bank, or skip.',
                            life: 3000,
                        });
                    } else {
                        // No AI Pre-Pop step to advance to -- finish here.
                        this.finishVendorWizard();
                    }


                    this.loading = false
                    return
                }
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error while updating vendor relationship',
                        detail: existingVendorResponse.data,
                        life: 3000,
                    });

                    this.loading = false;
            }
        },

        goToStep4() {
            // Only proceed if Step 3 validation passes
            if (this.isStep3Valid) {
                // Additional validation: ensure admin fields are populated from primary contact
                if (!this.adminFirstName || !this.adminEmail) {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Admin Fields Required',
                        detail: 'Admin fields must be populated from the primary contact before proceeding',
                        life: 3000,
                    });
                    return;
                }
                
                // Visibility Settings (Step 4) always comes right after
                // Vendor Relationship now, regardless of the AI Pre-Pop
                // feature flag -- AI Pre-Population (flag-gated) comes
                // after Visibility, not before it.
                this.currentStep = 4;
                this.$toast.add({
                    severity: 'success',
                    summary: this.getStepTitle(4),
                    detail: 'Proceeding to configure vendor sharing settings',
                    life: 3000,
                });
            } else {
                if (!this.contacts || this.contacts.length === 0) {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Validation Required',
                        detail: 'Please add at least one contact before proceeding',
                        life: 3000,
                    });
                } else if (!this.hasPrimaryContact) {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Primary Contact Required',
                        detail: 'Please mark at least one contact as primary before proceeding',
                        life: 3000,
                    });
                } else {
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Validation Required',
                        detail: 'Please ensure all required fields are filled before proceeding',
                        life: 3000,
                    });
                }
            }
        },

        goToStep3() {
            this.currentStep = 3;
            this.$toast.add({
                severity: 'success',
                summary: this.getStepTitle(3),
                detail: 'Manage company contacts',
                life: 3000,
            });
        },

        async goToStep3FromStep2() {
            console.log('Next button clicked, current step:', this.currentStep);
            console.log('Step 2 valid:', this.isStep2Valid);
            console.log('Name:', this.Name);
            console.log('Quickbooks ID:', this.quickbooksID);
            console.log('Description:', this.description);
            
            // Only proceed if validation passes
            if (this.isStep2Valid) {
                // Check if account code already exists
                try {
                    // Account code doesn't exist, proceed to step 3
                    this.currentStep = 3;
                    console.log('Moving to step 3, new current step:', this.currentStep);
                    this.$toast.add({
                        severity: 'success',
                        summary: this.getStepTitle(3),
                        detail: 'Proceeding to add company contacts',
                        life: 3000,
                    });
                } catch (error) {
                    console.error('Error checking account code:', error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to check account code. Please try again.',
                        life: 5000,
                    });
                }
            }
            // No else block needed since button is disabled when validation fails
        },

        // Build the complete payload for new partner creation
        buildPartnerPayload() {
            // Get accountId from localStorage
            const userDetails = JSON.parse(localStorage.getItem('pro_marketplaceUser'));
            const accountId = userDetails?.value?.account?.accountId;
            
            if (!accountId) {
                console.error('Account ID not found in localStorage');
                this.$toast.add({
                    severity: 'error',
                    summary: 'Configuration Error',
                    detail: 'Account ID not found. Please refresh the page and try again.',
                    life: 5000,
                });
                return null;
            }

            // Find the primary contact to populate admin fields
            const primaryContact = this.contacts.find(contact => contact.isPrimaryContact);
            
            if (!primaryContact) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Primary Contact Required',
                    detail: 'Please add at least one contact and mark it as primary before proceeding.',
                    life: 5000,
                });
                return null;
            }

            // Auto-populate admin fields from primary contact
            const adminFirstName = primaryContact.name.split(' ')[0] || '';
            const adminLastName = primaryContact.name.split(' ').slice(1).join(' ') || '';
            const adminEmail = primaryContact.email;

            // Build contacts array with proper structure
            const contacts = this.contacts.map(contact => ({
                id: this.generateContactId(), // Generate unique ID for each contact
                name: contact.name,
                email: contact.email,
                phone: contact.phone,
                title: contact.title,
                isPrimary: contact.isPrimaryContact,
                isEscalation: contact.isEscalationContact,
                isRFPContributor: contact.isRFPContributor,
                isRFPInvitationRecipient: contact.isRFPInvitationRecipient
            }));

            // Build visible_to array based on visibility settings
            let visible_to = [];
            if (this.vendorSharing.channelVisibility === 'public') {
                // All channel IDs for public
                visible_to = this.channels.map(channel => channel.subnetwork_id);
            } else if (this.vendorSharing.channelVisibility === 'select') {
                // Only selected channels
                visible_to = this.channels
                    .filter(channel => channel.selectedForSharing)
                    .map(channel => channel.subnetwork_id);

                // Auto-add current user's subnetwork ID
                if (!visible_to.includes(this.currentUserSubnetworkId)) {
                    visible_to.push(this.currentUserSubnetworkId);
                }
                
            } else if (this.vendorSharing.channelVisibility === 'private') {
                if (this.isChannel) {
                    visible_to = [this.subnetworkIdOfOtherChannels];
                } else {
                    visible_to = [this.defaultSubnetworkId];
                }
            }

            // Build channels array from enabled channels that are also in visible_to array
            const channels = this.channels
                .filter(channel => channel.enabled && visible_to.includes(channel.subnetwork_id))
                .map(channel => ({
                    subnetworkId: channel.subnetwork_id,
                    contractSubnetworkId: channel.selectedTemplate === 'default'
                        ? this.defaultSubnetworkId
                        : channel.subnetwork_id
                }));

            console.log("this.vendorSharing.channelVisibility", this.vendorSharing.channelVisibility, "isChannel", this.isChannel, "Other", this.subnetworkIdOfOtherChannels, "Default", this.defaultSubnetworkId, "vis", visible_to)

            // Build the complete payload
            const payload = {
                quickbooksID: this.quickbooksID,
                name: this.Name,
                description: this.description,
                isPartner: true,
                accreditedChannels: this.isAccredited ? [
                    'f4cd7264-3f00-463b-b2e9-d9e69c3ec193'
                ] : [],
                channels: channels,
                contacts: contacts,
                accountId: accountId,
                visibleTo: visible_to,
                visibleStatus: this.vendorSharing.channelVisibility,
                commission_rate: this.commissionRate ? `${this.commissionRate}%` : '',
                notes: this.notes || '',
                adminFirstName: adminFirstName,
                adminEmail: adminEmail,
                adminLastName: adminLastName
            };

            console.log('Partner Payload:', payload);
            return payload;
        },

        // Generate unique contact ID
        generateContactId() {
            return 'c' + Date.now() + Math.random().toString(36).substr(2, 9);
        },

        // Build the complete payload for existing vendor relationship updates
        buildExistingVendorPayload() {
            // Get accountId from localStorage
            const userDetails = JSON.parse(localStorage.getItem('pro_marketplaceUser'));
            const accountId = userDetails?.value?.account?.accountId;
            
            if (!accountId) {
                console.error('Account ID not found in localStorage');
                this.$toast.add({
                    severity: 'error',
                    summary: 'Configuration Error',
                    detail: 'Account ID not found. Please refresh the page and try again.',
                    life: 5000,
                });
                return null;
            }

            if (!this.selectedPartner) {
                console.error('No partner selected for relationship update');
                this.$toast.add({
                    severity: 'error',
                    summary: 'Vendor Selection Error',
                    detail: 'No vendor selected. Please search and select a vendor first.',
                    life: 5000,
                });
                return null;
            }

            // Build contacts array with proper structure
            const contacts = this.contacts.map(contact => ({
                id: this.generateContactId(), // Generate unique ID for each contact
                name: contact.name,
                email: contact.email,
                phone: contact.phone,
                title: contact.title,
                isPrimary: contact.isPrimaryContact,
                isEscalation: contact.isEscalationContact,
                isRFPContributor: contact.isRFPContributor,
                isRFPInvitationRecipient: contact.isRFPInvitationRecipient
            }));

            // Build visible_to array based on visibility settings
            let visible_to = [];
            if (this.vendorSharing.channelVisibility === 'public') {
                // All channel IDs for public
                visible_to = this.channels.map(channel => channel.subnetwork_id);
            } else if (this.vendorSharing.channelVisibility === 'select') {
                // Only selected channels
                visible_to = this.channels
                    .filter(channel => channel.selectedForSharing)
                    .map(channel => channel.subnetwork_id);

                // Auto-add current user's subnetwork ID
                if (!visible_to.includes(this.currentUserSubnetworkId)) {
                    visible_to.push(this.currentUserSubnetworkId);
                }

            } else if (this.vendorSharing.channelVisibility === 'private') {
                if (this.isChannel) {
                    visible_to = [this.subnetworkIdOfOtherChannels];
                } else {
                    visible_to = [this.defaultSubnetworkId];
                }
            }



            // Build the complete payload for existing vendor relationship
            const payload = {
                partnerId: this.selectedPartner.id, // ID of the existing partner
                partnerName: this.selectedPartner.name, // Name of the existing partner
                accountId: accountId, // Current user's account ID
                contacts: contacts, // Updated contacts for the relationship
                commissionRate: this.commissionRate ? `${this.commissionRate}%` : '',
                notes: this.notes || '',
                distributorId: this.partnerDistributorId,
                visibleTo: visible_to,
                visibleStatus: this.vendorSharing.channelVisibility,
                // For existing vendors, we don't update basic info like name, description, etc.
                // Only the relationship-specific data (contacts) is updated
            };

            console.log('Existing Vendor Relationship Payload:', payload);
            return payload;
        },

        async saveNewVendor() {
            // Build the complete payload
            const payload = this.buildPartnerPayload();
            
            if (!payload) {
                return; // Error already shown in buildPartnerPayload
            }


            this.loading = true;

            const partnerCreationResponse = await this.accountMangementService.addNewUser(payload);

            if (partnerCreationResponse.status == 200) {

                let userCreationPayload = {
                    email: payload.adminEmail,
                    account_id: partnerCreationResponse.data.accountID,
                    user_role_id: USER_ROLE_IDS.PARTNER,
                    last_name: payload.adminLastName,
                    first_name: payload.adminFirstName,
                    // Only this "New Partner" primary contact gets Cognito's
                    // email suppressed in favor of the app's own welcome
                    // email (see finalizeNewVendor below) -- RFP Contributors
                    // and every other caller of this endpoint keep Cognito's
                    // default.
                    send_combined_welcome_email: true,
                }
                await this.userManagementService.addNewUser(userCreationPayload);

                // Create users for all RFP Contributors (excluding Primary Contact)
                const rfpContributors = this.contacts.filter(contact => 
                    contact.isRFPContributor && !contact.isPrimaryContact
                );
                
                for (const rfpContributor of rfpContributors) {
                    const rfpUserPayload = {
                        email: rfpContributor.email,
                        account_id: partnerCreationResponse.data.accountID,
                        user_role_id: USER_ROLE_IDS.CONTRIBUTOR, // RFP Contributor role
                        last_name: rfpContributor.name.split(' ').slice(1).join(' ') || '',
                        first_name: rfpContributor.name.split(' ')[0] || '',
                    };
                    console.log('------>User creation payload for RFP Contributor (New Partner):', rfpUserPayload);
                    await this.userManagementService.addNewUser(rfpUserPayload);
                }

                this.$toast.add({
                    severity: 'success',
                    summary: 'Vendor Created Successfully',
                    detail: 'Vendor created successfully',
                    life: 3000,
                });

                if (this.aiPrePopEnabled) {
                    // Advance into AI Pre-Population (now the last step)
                    // instead of resetting the wizard. The vendor record
                    // now exists, so give AiPrePopulationStep a real
                    // account_id via selectedPartner -- the same field/shape
                    // the existing-vendor branch already relies on.
                    this.selectedPartner = {
                        account_id: partnerCreationResponse.data.accountID,
                        name: this.Name,
                    };
                    this.currentStep = 5;
                    this.$toast.add({
                        severity: 'success',
                        summary: this.getStepTitle(5),
                        detail: 'Optional — provide source material to pre-populate the Answer Bank, or skip.',
                        life: 3000,
                    });
                } else {
                    // No AI Pre-Pop step to advance to -- finish here. The
                    // welcome email is still mandatory (it's the vendor's
                    // only way to get their credentials, since Cognito's
                    // own email is suppressed), so it has to fire from this
                    // branch too -- finishVendorWizard() alone can't send it
                    // here since selectedPartner was never set in this path.
                    try {
                        await this.vendorSourceService.finalizeNewVendor(partnerCreationResponse.data.accountID);
                    } catch (err) {
                        console.error('finalizeNewVendor failed', err);
                    }
                    this.finishVendorWizard();
                }

                this.loading = false;
                return;
            }
            this.$toast.add({
                severity: 'error',
                summary: 'Error while creating vendor',
                detail: partnerCreationResponse.data,
                life: 3000,
            });
            this.loading = false;
        },

        completePartnerSetup() {
            // Here you can add logic to save all partner data
            this.$toast.add({
                severity: 'success',
                summary: 'Vendor Setup Complete',
                detail: 'Vendor has been successfully configured',
                life: 3000,
            });
            
            // Reset to initial state
            this.currentStep = 1;
            this.selectedPartner = null;
            this.resetForm();
            this.contacts = [];
            this.vendorSharing = {
                channelVisibility: 'public'
            };
        },

        getStepClass(stepNumber) {
            // For existing vendor flow, adjust step numbers
            if (!this.isNewVendor) {
                if (stepNumber === 1) {
                    // Step 1 (Search) - always accessible
                    if (this.currentStep > 1) {
                        return 'completed';
                    } else if (this.currentStep === 1) {
                        return 'current';
                    } else {
                        return 'incomplete';
                    }
                } else if (stepNumber === 2) {
                    // Step 2 (Vendor Relationship) - maps to currentStep 3
                    if (this.currentStep > 3) {
                        return 'completed';
                    } else if (this.currentStep === 3) {
                        return 'current';
                    } else {
                        return 'incomplete';
                    }
                } else if (stepNumber === 3) {
                    // Slot 3 = Visibility Settings (currentStep 4), always.
                    if (this.currentStep > 4) return 'completed';
                    if (this.currentStep === 4) return 'current';
                    return 'incomplete';
                } else if (stepNumber === 4) {
                    // Slot 4 only exists when AI Pre-Pop is enabled — it's
                    // AI Pre-Population (currentStep 5), the last step.
                    if (this.currentStep > 5) return 'completed';
                    if (this.currentStep === 5) return 'current';
                    return 'incomplete';
                }
            }
            
            // For new vendor flow, use original logic
            if (this.currentStep > stepNumber) {
                return 'completed';
            } else if (this.currentStep === stepNumber) {
                return 'current';
            } else {
                return 'incomplete';
            }
        },

        navigateToStep(stepNumber) {
            console.log('Navigating to step:', stepNumber);

            // Map stepper step numbers to actual step numbers
            let actualStepNumber = stepNumber;

            // For existing vendor flow, map stepper indicator numbers to
            // internal step numbers (Step 2 = Create New Vendor is skipped).
            // Slot 3 is always Visibility Settings now; slot 4 (AI
            // Pre-Population, last step) only exists when the flag is on.
            if (!this.isNewVendor) {
                if (stepNumber === 1) {
                    actualStepNumber = 1; // Search
                } else if (stepNumber === 2) {
                    actualStepNumber = 3; // Vendor Relationship
                } else if (stepNumber === 3) {
                    actualStepNumber = 4; // Visibility Settings
                } else if (stepNumber === 4) {
                    actualStepNumber = 5; // AI Pre-Population (only reachable when flag on)
                }
            }
            // New vendor flow: currentStep already matches the stepper slot
            // 1:1, no remapping needed.
            
            // Only allow backward navigation to completed steps
            if (actualStepNumber < this.currentStep) {
                // Allow navigation to previous steps
                this.currentStep = actualStepNumber;
                
                // If navigating back to step 1, reset the form and flags
                if (actualStepNumber === 1) {
                    this.selectedPartner = null;
                    this.isNewVendor = false;
                    this.resetForm();
                }
                
                this.$toast.add({
                    severity: 'success',
                    summary: this.getStepTitle(actualStepNumber),
                    detail: 'Navigating to previous step',
                    life: 3000,
                });
            } else if (actualStepNumber === this.currentStep) {
                // Already on current step, no action needed
                return;
            } else {
                // Prevent forward navigation
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Step Locked',
                    detail: 'Please complete the current step before proceeding',
                    life: 3000,
                });
            }
        },
        
        getDisplayStepNumber(stepNumber) {
            // Map the internal step number to the stepper indicator label.
            // AI Pre-Population is the last step in both flows, so it's
            // simply absent from currentStepperSteps (and never rendered)
            // when the feature flag is off -- no remapping needed for it.
            if (!this.isNewVendor) {
                if (stepNumber === 1) return 1;
                if (stepNumber === 3) return 2;
                if (stepNumber === 4) return 3; // Visibility Settings
                if (stepNumber === 5) return 4; // AI Pre-Pop (flag on only)
                return stepNumber;
            }
            // New vendor flow: currentStep already matches the stepper slot 1:1.
            return stepNumber;
        },
        
        getStepTitle(stepNumber) {
            // Map step numbers to display step numbers based on flow type.
            // Total step count drops by one when AI Pre-Pop is disabled.
            let displayStepNumber = stepNumber;
            let stepName = '';
            const totalSteps = this.currentStepperSteps.length;

            if (!this.isNewVendor) {
                if (stepNumber === 1) {
                    displayStepNumber = 1;
                    stepName = 'Search for Existing Vendor';
                } else if (stepNumber === 3) {
                    displayStepNumber = 2;
                    stepName = 'Vendor Relationship Information';
                } else if (stepNumber === 4) {
                    displayStepNumber = 3;
                    stepName = 'Visibility Settings';
                } else if (stepNumber === 5) {
                    displayStepNumber = 4;
                    stepName = 'AI Answer Bank Pre-Population';
                }
                return `Step ${displayStepNumber} of ${totalSteps}: ${stepName}`;
            } else {
                if (stepNumber === 1) {
                    stepName = 'Search for Existing Vendor';
                } else if (stepNumber === 2) {
                    stepName = 'Create New Vendor';
                } else if (stepNumber === 3) {
                    stepName = 'Vendor Relationship Information';
                } else if (stepNumber === 4) {
                    stepName = 'Visibility Settings';
                } else if (stepNumber === 5) {
                    stepName = 'AI Answer Bank Pre-Population';
                }
                return `Step ${displayStepNumber} of ${totalSteps}: ${stepName}`;
            }
        },

        // Wizard completion, called from the (now-last) AI Pre-Population
        // step -- either the vendor was just created/updated at Visibility
        // Settings and the user skipped or finished pre-population, or the
        // AI Pre-Pop feature flag is off and Visibility Settings' own save
        // handler calls this directly since there's no further step.
        // Emitted by AiPrePopulationStep on Skip, and bound to the
        // "Finish" button.
        async finishVendorWizard() {
            // Defensive: the Skip/Finish buttons are already disabled while
            // AI Pre-Population is running, but guard here too in case this
            // is ever reached another way -- see isAiRunInProgress.
            if (this.isAiRunInProgress) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'AI Pre-Population in progress',
                    detail: 'Please wait for the current run to finish before continuing.',
                    life: 4000,
                });
                return;
            }

            // For a brand-new vendor, this is what actually sends their one
            // combined welcome email (credentials + pre-population summary)
            // -- read before the state below clears selectedPartner/isNewVendor.
            // Best-effort: a failure here must not block closing the wizard,
            // since the vendor account itself was already created successfully.
            if (this.isNewVendor && this.selectedPartner?.account_id) {
                try {
                    await this.vendorSourceService.finalizeNewVendor(this.selectedPartner.account_id);
                } catch (err) {
                    console.error('finalizeNewVendor failed', err);
                }
            }

            this.currentStep = 1;
            this.selectedPartner = null;
            this.isNewVendor = false;
            this.aiRunState = { runInProgress: false, lastRun: null };
            this.resetForm();
            this.contacts = [];
            this.vendorSharing = {
                channelVisibility: 'public'
            };
            EventBus.emit('refresh-menu');
            this.$toast.add({
                severity: 'success',
                summary: 'Step 1: Search for Existing Vendor',
                detail: 'Vendor setup complete',
                life: 3000,
            });
        },

        // Called by AiPrePopulationStep when the source list changes.
        // Reserved for future hooks (e.g. enabling a primary Run-AI button
        // at the wizard level). No-op for now.
        // eslint-disable-next-line no-unused-vars
        onSourcesChanged(payload) {
            // intentional no-op — reserved for Stage 2/3 wiring
        },

        // Keeps aiRunState in sync with AiPrePopulationStep's own
        // runInProgress/lastRun -- see the aiRunState data() comment for
        // why this can't just be read via $refs in a computed.
        onAiRunStateChanged(payload) {
            this.aiRunState = payload;
        }
    },
};
</script>

<style scoped>
:deep(.btn) {
    width: 8rem !important;
}

:deep(.card) {
    margin-top: 1.5rem;
}

.subtitle {
    font-size: 1rem;
}

.error-msg {
    margin-left: 1.25rem;
    color: red;
    font-size: 1rem;
    font-weight: 400;
}

.radio-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    max-width: 22rem;
    margin-left: 1rem;
}

.radioLabel {
    margin-left: 1rem;
}

.radioLabel.disabled {
    color: #999;
    cursor: not-allowed;
}

::v-deep(.p-inputtext):not(.partner-search-step .p-inputtext):not(.contact-form .p-inputtext),
::v-deep(.p-dropdown):not(.partner-search-step .p-dropdown):not(.contact-form .p-dropdown) {
    margin-left: 1rem;
    max-width: 30rem;
}

.title {
    font-weight: 700;
    font-family: 'Prompt', sans-serif;
    font-size: 27px;
}

.field-title {
    margin-left: 1rem;
    font-size: 1.2rem;
    font-weight: 400;
    max-width: 30rem;
}

/* Table styles */
.channels-table {
    width: 100%;
    border-collapse: collapse;
    margin-left: 10px;
    margin-top: 10px;
}

.channels-table th,
.channels-table td {
    padding: 10px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

/* Column styles */
.switch-column {
    width: 10%;
}

.channel-name-column {
    width: 40%;
    font-size: 14px;
}

.template-column {
    width: 50%;
}

/* Dropdown styles */
/* Ensure the dropdown fills the cell */
.template-dropdown {
    width: 100% !important;
    padding: 5px;
    font-size: 14px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: white;
    box-sizing: border-box;
}

/* Style when dropdown is focused (active state) */
.template-dropdown:focus {
    outline: none;
    border-color: #007bff;
    /* Add blue border on focus */
    box-shadow: 0 0 5px rgba(0, 123, 255, 0.5);
    /* Subtle blue glow effect */
    background-color: white;
    /* Keep background white on focus */
    color: black;
    /* Ensure text color is black */
}

/* Change the caret (dropdown arrow) color if needed */
.template-dropdown::-ms-expand {
    color: black;
}

/* Optional - Style hover and active states for better UX */
.template-dropdown:hover {
    border-color: #007bff;
    /* Blue border on hover */
}

.template-dropdown:active {
    border-color: #007bff;
}

.channels-table th {
    font-weight: 400;
}

.template-dropdown option {
    border-radius: 0px;
    /* Remove any border-radius on the expanded options */
    background-color: white;
    color: black;
}

.width-30-rem {
    max-width: 30rem;
}

.ml-1rem {
    margin-left: 1rem;
}

/* Customize the dropdown open list */
.template-dropdown:focus option {
    border-radius: 0px;
    /* Ensure no rounded corners for the dropdown list */
    background-color: white;
    color: black;
}

.mr-1rem {
    margin-right: 1rem;
}

.upload-container {
    margin-bottom: 1rem;
}



:deep(.uploader) {
    display: block !important;
    width: 100% !important;
}


:deep(.uploader:hover) {
    background-color: #00796b;
}

.upload-container small {
    color: #666;
}

.upload-loader {
    text-align: center;
    padding: 1rem;
}

.upload-loader p {
    margin-top: 1rem;
    color: #666;
}

.upload-container small {
    display: block;
    margin-top: 0.5rem;
    color: #666;
}

.upload-results {
    padding: 1rem;
}

.result-section {
    margin-bottom: 1.5rem;
}

.result-list {
    list-style: none;
    padding-left: 0;
    margin: 0.5rem 0;
}

.result-list li {
    padding: 0.5rem;
    margin: 0.25rem 0;
    border-radius: 4px;
    font-size: 0.9rem;
}

.success-header {
    color: #2196F3;
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
}

.error-header {
    color: #f44336;
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
}

.success-list li {
    background-color: #e3f2fd;
    color: #1565c0;
}

.error-list li {
    background-color: #ffebee;
    color: #c62828;
}

.warning-message {
    font-size: 0.8rem;
    color: #ff9800;
    font-style: italic;
    margin-top: 0.25rem;
}

.mt-2 {
    margin-top: 1rem;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.spinner {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-left-color: #007bff;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.industry-heading {
    font-weight: bold;
    font-size: 1.2rem;
    margin-top: 1rem;
}

.industry-underline {
    border: none;
    border-bottom: 2px solid #ccc;
    margin: 0.5rem 0;
}

.industryOption {
    padding-left: 1rem;
    font-size: 1rem;
    color: #333;
}

::v-deep(.p-dropdown .p-dropdown-label) {
    margin-left: 0rem;
}

.mt-1rem {
    margin-top: 1rem;
}

.mb-1rem {
    margin-bottom: 1rem;
}

.partner-search-step {
    margin: 2rem 0;
}

.back-to-search {
    margin: 1rem 0;
    padding: 0 1rem;
}

.back-to-search .p-button {
    color: #007bff;
    font-weight: 500;
    white-space: nowrap !important;
    min-width: fit-content !important;
    /* Remove PrimeVue's internal 1rem left padding so the "←" arrow
       sits flush with the wrapper's 1rem padding — that lines the
       back link up with the .step-title h4 below it. */
    padding-left: 0 !important;
}

.back-to-search .p-button:hover {
    background-color: #f8f9fa;
}

.step-title {
    margin: 1rem 0 1.5rem 0;
    padding: 0 1rem;
}

.step-title h4 {
    color: #333;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;
}

/* Company Contacts Styles */
.company-contacts-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #f0f9ff;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
}

.contacts-title {
    display: flex;
    align-items: center;
}

.contacts-title h4 {
    margin: 0;
    color: #333;
    font-size: 1.2rem;
    font-weight: 600;
}

.table-wrap {
    margin-top: 2rem;
    margin-bottom: 2rem;
    overflow-x: scroll;
}


.contacts-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.contacts-table th,
.contacts-table td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #eee;
    max-width: 200px;
    word-wrap: break-word;
    white-space: normal;
}

.contacts-table th {
    background: #fbfbfb;
    color: #707683;
    font-weight: 500;
}

.center-column {
    text-align: center;
}

.center-column i {
    font-size: 1.1rem;
}

.pi-check {
    color: #00796b;
}

.pi-times {
    color: #707683;
}


.mb-1 {
    margin-bottom: 1rem;
}

.actions-column-inner {
    display: flex;
}

.contacts-table td.no-contacts {
    text-align: center;
    color: #666;
    font-style: italic;
    padding: 2rem;
}

/* Highlights the contact row when navigating in from the suppression report. */
.contacts-table tr.highlight-contact {
    background-color: #fff4e6;
    box-shadow: inset 4px 0 0 #f57c00;
}
.contacts-table tr.highlight-contact td {
    background-color: #fff4e6;
}

/* Contact Dialog Styles */
.contact-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.form-field label {
    font-weight: 500;
    color: #333;
    font-size: 0.9rem;
}

.checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
}

.checkbox-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.checkbox-item label {
    font-weight: 400;
    color: #333;
    cursor: pointer;
}

.disabled-label {
    color: #999 !important;
    cursor: not-allowed !important;
}

.disabled-note {
    font-size: 0.8rem;
    color: #999;
    font-style: italic;
    margin-left: 0.5rem;
}

.w-full {
    width: 100%;
}

.add-contact-button {
    background-color: #00796b !important;
    border-color: #00796b !important;
    white-space: nowrap;
    min-width: fit-content;
}

.add-contact-button:hover {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

.add-contact-button:focus {
    background-color: #00796b !important;
    border-color: #00796b !important;
    box-shadow: 0 0 0 2px rgba(0, 121, 107, 0.2) !important;
}

/* Dialog button styles */
.dialog-cancel-button {
    background-color: transparent !important;
    border-color: #00796b !important;
    color: #00796b !important;
}

.dialog-cancel-button:hover {
    background-color: transparent !important;
    border-color: #00796b !important;
    color: #00796b !important;
}

.dialog-action-button {
    background-color: #00796b !important;
    border-color: #00796b !important;
    color: white !important;
}

.dialog-action-button:hover {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

/* Step Navigation Styles */
.step-navigation {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 2rem;
    padding: 0 1rem;
    flex-wrap: wrap;
}

.step2-navigation {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 2rem;
    padding: 0 1rem;
    width: 100%;
    position: relative;
    flex-wrap: wrap;
}

.next-button {
    background-color: #00796b !important;
    border-color: #00796b !important;
    color: white !important;
    white-space: nowrap !important;
    min-width: fit-content !important;
}

.next-button:hover:not(:disabled) {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

.next-button:disabled {
    background-color: #cccccc !important;
    border-color: #cccccc !important;
    color: #666666 !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
}

/* Step 3 Next button specific styling */
.step-navigation .next-button:disabled {
    background-color: #cccccc !important;
    border-color: #cccccc !important;
    color: #666666 !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
}

.previous-button {
    background-color: transparent !important;
    border-color: #00796b !important;
    color: #00796b !important;
}

.previous-button:hover {
    background-color: transparent !important;
    border-color: #00796b !important;
    color: #00796b !important;
}

.complete-button {
    background-color: #00796b !important;
    border-color: #00796b !important;
    color: #ffffff !important;
    white-space: nowrap !important;
    min-width: fit-content !important;
}

.complete-button:hover {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

.save-button {
    background-color: #00796b !important;
    border-color: #00796b !important;
    color: white !important;
    white-space: nowrap !important;
    min-width: fit-content !important;
}

.save-button:hover:not(:disabled) {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

.save-button:disabled {
    background-color: #cccccc !important;
    border-color: #cccccc !important;
    color: #666666 !important;
    cursor: not-allowed !important;
    opacity: 0.6 !important;
}



/* Vendor Sharing Styles */
.vendor-sharing-container {
    background: white;
    border-radius: 8px;
    padding: 2rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.sharing-header {
    margin-bottom: 2rem;
}

.sharing-header h4 {
    color: #333;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
}

.sharing-description {
    color: #666;
    font-size: 1rem;
    margin: 0;
    line-height: 1.5;
}

.sharing-options {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.sharing-option {
    padding: 1.5rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #fafafa;
}

.option-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
}

.option-label {
    font-weight: 600;
    color: #333;
    font-size: 1.1rem;
    cursor: pointer;
}

.option-description {
    color: #666;
    font-size: 0.9rem;
    margin: 0;
    line-height: 1.4;
    padding-left: 2rem;
}

/* Channel Visibility Styles */
.channel-visibility-section {
    margin-bottom: 2rem;
    padding: 1.5rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #fafafa;
}

.channel-visibility-section h5 {
    color: #333;
    font-size: 1.3rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
}

.section-description {
    color: #666;
    font-size: 0.9rem;
    margin: 0 0 1.5rem 0;
    line-height: 1.4;
}

.visibility-options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.visibility-option {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;
}

.visibility-option:hover {
    border-color: #00796b;
    background-color: #f0f9ff;
}

.visibility-label {
    font-weight: 600;
    color: #333;
    font-size: 1rem;
    cursor: pointer;
    margin: 0;
}

.visibility-description {
    color: #666;
    font-size: 0.85rem;
    margin: 0.25rem 0 0 0;
    line-height: 1.4;
    flex: 1;
}

.channel-display-section {
    margin-top: 1.5rem;
    padding: 1.5rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: white;
}

.channel-display-section h6 {
    color: #333;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0 0 1rem 0;
}

/* Public Channels */
.public-channels .channel-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.channel-item.public-channel {
    display: flex;
    align-items: center;
    padding: 0.75rem;
    background-color: #f0f9ff;
    border: 1px solid #00796b;
    border-radius: 6px;
    color: #00796b;
}

.status-badge {
    margin-left: auto;
    background-color: #00796b;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
}

/* Private Channels */
.private-message {
    text-align: center;
    padding: 2rem;
    color: #666;
}

.private-message h6 {
    color: #333;
    margin: 0 0 0.5rem 0;
}

.private-message p {
    margin: 0;
    font-size: 0.9rem;
}

/* Select Channels */
.channel-selection-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.channel-selection-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background-color: #fafafa;
}

.channel-selection-label {
    font-weight: 500;
    color: #333;
    cursor: pointer;
    margin: 0;
}

/* Stepper Styles */
.stepper-container {
    margin-bottom: 2rem;
    padding: 1.5rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stepper {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    max-width: 900px;
    margin: 0 auto;
    padding: 0 20px;
}

.stepper::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 30px;
    right: 30px;
    height: 2px;
    background-color: #e0e0e0;
    z-index: 1;
}

.stepper-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;
    flex: 1;
    min-width: 0;
    transition: all 0.3s ease;
}

.stepper-step.completed,
.stepper-step.current {
    cursor: pointer;
}

.stepper-step.incomplete {
    cursor: not-allowed;
}

.step-indicator {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 0.75rem;
    border: 2px solid;
    transition: all 0.3s ease;
    flex-shrink: 0;
}

.step-number {
    color: #ffffff;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.step-check {
    color: white;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
}

.step-label {
    font-size: 0.85rem;
    font-weight: 500;
    text-align: center;
    color: #666;
    max-width: 140px;
    line-height: 1.3;
    word-wrap: break-word;
    hyphens: auto;
}

/* Step States */
.stepper-step.completed .step-indicator {
    background-color: #00796b;
    border-color: #00796b;
    color: white;
}

.stepper-step.completed .step-label {
    color: #00796b;
    font-weight: 600;
}

.stepper-step.current .step-indicator {
    background-color: #00796b;
    border-color: #00796b;
    color: white;
}

.stepper-step.current .step-label {
    color: #00796b;
    font-weight: 600;
}

.stepper-step.incomplete .step-indicator {
    background-color: white;
    border-color: #e0e0e0;
    color: #999;
}

.stepper-step.incomplete .step-label {
    color: #999;
}

/* Hover Effects for Clickable Steps */
.stepper-step.completed:hover,
.stepper-step.current:hover {
    transform: translateY(-2px);
}

.stepper-step.completed:hover .step-indicator,
.stepper-step.current:hover .step-indicator {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.stepper-step.completed:hover .step-indicator {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

.stepper-step.current:hover .step-indicator {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

/* No hover effects for incomplete steps */
.stepper-step.incomplete:hover {
    transform: none;
}

.stepper-step.incomplete:hover .step-indicator {
    box-shadow: none;
}

/* Responsive Design */
@media (max-width: 768px) {
    .stepper-container {
        padding: 1rem;
    }
    
    .stepper {
        flex-direction: column;
        gap: 1.5rem;
        padding: 0;
    }
    
    .stepper::before {
        display: none;
    }
    
    .stepper-step {
        flex-direction: row;
        gap: 1rem;
        width: 100%;
        justify-content: flex-start;
        align-items: center;
    }
    
    .step-label {
        text-align: left;
        max-width: none;
        flex: 1;
        line-height: 1.2;
    }
    
    .step-indicator {
        margin-bottom: 0;
        flex-shrink: 0;
    }
    
    /* Responsive navigation buttons for tablets */
    .step-navigation,
    .step2-navigation {
        gap: 12px;
        padding: 0 0.75rem;
    }
    
    .step-navigation .p-button,
    .step2-navigation .p-button {
        padding: 0.6rem 1rem;
    }
}

@media (max-width: 480px) {
    .stepper-container {
        padding: 0.75rem;
    }
    
    .step-label {
        font-size: 0.8rem;
        max-width: 200px;
    }
    
    .step-indicator {
        width: 36px;
        height: 36px;
    }
    
    .step-number {
        font-size: 0.9rem;
    }
    
    .step-check {
        font-size: 1rem;
    }
    
    /* Responsive navigation buttons */
    .step-navigation,
    .step2-navigation {
        justify-content: center;
        gap: 8px;
        padding: 0 0.5rem;
    }
    
    .step-navigation .p-button,
    .step2-navigation .p-button {
        min-width: auto;
        padding: 0.5rem 0.75rem;
        font-size: 0.9rem;
    }
}

/* Custom Delete Confirmation Dialog Styles */
.delete-confirmation-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 0;
}

/* Primary Contact Replacement Confirmation Dialog Styles */
.confirmation-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 0;
}

.delete-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 50%;
    flex-shrink: 0;
}

.delete-icon i {
    font-size: 1.5rem;
    color: #f39c12;
}

.delete-message {
    margin: 0;
    color: #333;
    font-size: 1rem;
    line-height: 1.5;
}

.delete-cancel-button {
    background-color: transparent !important;
    border-color: #6c757d !important;
    color: #6c757d !important;
}

.delete-cancel-button:hover {
    background-color: transparent !important;
    border-color: #5a6268 !important;
    color: #5a6268 !important;
}

.delete-confirm-button {
    background-color: #00796b !important;
    border-color: #00796b !important;
    color: white !important;
}

.delete-confirm-button:hover {
    background-color: #00695c !important;
    border-color: #00695c !important;
}

/* Disabled input field styles */
.disabled-input {
    background-color: #f8f9fa !important;
    color: #666 !important;
    cursor: not-allowed !important;
    opacity: 0.7 !important;
}

.disabled-input:focus {
    box-shadow: none !important;
    border-color: #e0e0e0 !important;
}

::v-deep(.p-button.p-button-icon-only.p-button-rounded) {
    border-radius: 50%;
    height: 2.5rem;
    width: 2.5rem !important;
    margin: 0.5rem;
    background: #ffffff !important;
    border: none !important;
    display: flex;
    float: left !important;
}

::v-deep(.p-button.p-button-icon-only.p-button-rounded):hover {
    background: #e4edd9 !important;
}

::v-deep(.p-button.p-button-danger) {
    background-color: #dc3545 !important;
    color: #ffffff !important;
    border: 1px solid #dc3545 !important;
}

::v-deep(.p-button.p-button-danger:hover) {
    background-color: #c82333 !important;
    border-color: #bd2130 !important;
}

.remove-active-style:enabled:active{
    background: #00796b !important;
}

.remove-active-style:enabled:focus {
    background: #00796b !important;
}

.p-fileupload-choose:not(.p-disabled):active{
    background: #00796b !important;
    color: #ffffff;
    border-color: transparent;
}

/* Step 4 (AI Pre-Population) — navigation footer.
   Skip + Continue sit on the same row, right-aligned, with the Skip
   button as the secondary action to the left of Continue. */
.ai-prepop-nav-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.5rem;
}
/* Override the global `.btn { width: 8rem }` from earlier in this file
   so both labels fit on one line. */
.ai-prepop-nav-footer :deep(.p-button) {
    width: auto !important;
    min-width: 8rem;
    white-space: nowrap;
}
.ai-prepop-continue-btn {
    background: #008a83 !important;
    border-color: #008a83 !important;
}
.ai-prepop-skip-btn {
    color: #6c757d;
}
.ai-prepop-run-wait-hint {
    margin-right: auto;
    color: #6c757d;
    font-style: italic;
}

/* Step 4 — deferred-availability message (new vendor branch) */
.ai-prepop-deferred {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    padding: 1.25rem 1.5rem;
    background: #f1faf3;
    border: 1px solid #cce8d4;
    border-radius: 8px;
    color: #2e5a39;
    line-height: 1.5;
}
.ai-prepop-deferred-icon {
    font-size: 1.4rem;
    color: #008a83;
    flex-shrink: 0;
}
.ai-prepop-deferred p {
    margin: 0.5rem 0 0 0;
}

</style>
